package org.multiplatform.kotfolio.pages.compose

import androidx.compose.runtime.Composable
import org.multiplatform.kotfolio.components.KotlinCode
import org.multiplatform.kotfolio.components.WebImage
import org.multiplatform.kotfolio.layout.BlogLayout
import org.multiplatform.kotfolio.layout.BlogProperties
import org.multiplatform.kotfolio.layout.PageProperties
import org.multiplatform.kotfolio.util.Resources
import com.varabyte.kobweb.compose.css.FontSize
import com.varabyte.kobweb.compose.css.FontWeight
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.borderRadius
import com.varabyte.kobweb.compose.ui.modifiers.fillMaxWidth
import com.varabyte.kobweb.compose.ui.modifiers.fontSize
import com.varabyte.kobweb.compose.ui.modifiers.fontWeight
import com.varabyte.kobweb.compose.ui.modifiers.padding
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.core.Page
import com.varabyte.kobweb.silk.components.text.SpanText
import org.jetbrains.compose.web.css.em
import org.jetbrains.compose.web.css.px
import org.jetbrains.compose.web.dom.Li
import org.jetbrains.compose.web.dom.Ol
import org.jetbrains.compose.web.dom.Ul

@Page("/compose/state_manage")
@Composable
fun StateManageComposePage() {
    BlogLayout(
        PageProperties(
            "State Compose",
            "A state is an object it can hold our data. If data changes happen, it will update all its subscribed UI compose. If you want to update the data at runtime in your compose, you can use the state object."
        ),
        BlogProperties("Jetpack Compose", "State Manage Compose")
    ) {
        Column(
            horizontalAlignment = Alignment.Start
        ) {
            SpanText(
                text = "Stop Using mutableListOf (Use buildList, buildMap, buildSet)",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "buildList takes a lambda where this is a MutableList. When the lambda returns, the list is sealed into a read-only List and returned. The mutable state never leaks:",
                        modifier = Modifier.fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "buildMap works identically, but this inside the lambda is a MutableMap. This is especially useful when your map construction involves non-trivial logic.",
                        modifier = Modifier.fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "uildSet gives you a MutableSet inside the builder and returns a read-only Set. It shines when you want to merge collections while automatically removing duplicates.",
                        modifier = Modifier.fontSize(FontSize.Medium)
                    )
                }
            }

            KotlinCode(
                code = """
                    val numbers = buildList {
                        add(1)
                        add(2)
                        addAll(listOf(3, 4, 5))
                        
                        if (System.currentTimeMillis() % 2 == 0L) add(6) // conditional logic is fine
                    }
                    """.trimIndent()
            )

            KotlinCode(
                code = """
                    //MutableList operation inside the block: add, addAll, remove, set, sortWith, and so on. 
                    fun getActiveUsers(users: List<user>): List<user> = buildList { 
                        for (user in users) {
                            if (user.isActive) add(user)
                        }
                    }
                    """.trimIndent()
            )

            SpanText(
                text = "Collection Hierarchy",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            KotlinCode(
                code = """
                    # kotlinx-collections-immutable
                    kotlinx-collections-immutable = "0.4.0"
                    kotlinx-collections-immutable = { module = "org.jetbrains.kotlinx:kotlinx-collections-immutable", version.ref = "kotlinx-collections-immutable" }

                    +------+--------------+---------------------+
                    |      |  Immutable   |       Mutable       |
                    +------+--------------+---------------------+
                    | List | listOf<T>()  | mutableListOf<T>()  |
                    | Set  | setOf<T>()   | mutableSetOf<T>()   |
                    | Map  | mapOf<K,V>() | mutableMapOf<K,V>() |
                    +------+--------------+---------------------+
                    """.trimIndent()
            )

            SpanText(
                text = "Kotlin 2.3.0 (Explicit backing fields)",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "A backing field is the hidden storage generated by Kotlin for a property when that property needs to store data. А property is the public АРІ exposed by the class. A field is the hidden memory that stores the property's value.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            KotlinCode(
                code = """
                    //KMP Each Module to Add
                    kotlin {
                        androidTarget {
                            compilerOptions {
                                jvmTarget.set(JvmTarget.JVM_11)
                            }
                        }
                        
                        //1
                        compilerOptions {
                            freeCompilerArgs.addAll("-Xexplicit-backing-fields", "-Xexpect-actual-classes", "-Xcontext-parameters")
                        }
                        
                        //2
                        sourceSets.all { languageSettings.optIn("ExplicitBackingFields") }

                        targets.configureEach {
                            compilations.configureEach {
                                compileTaskProvider.get().compilerOptions {
                                    freeCompilerArgs.addAll("-Xexpect-actual-classes", "-Xcontext-parameters")
                                }
                            }
                        }
                    }

                    //Explicit backing fields 
                    //Two properties for one piece of state
                   
                    class DetailsViewModel() : ViewModel() {
                        //1
                        val state: StateFlow <DetailsUiState>
                            field = MutableStateFlow<DetailsUiState>(DetailsUiState.Loading)
                            
                        //2
                        val city: StateFlow<String>
                            field = MutableStateFlow("")

                        fun updateCity(newCity: String) {
                            // Smart casting works automatically
                            city.value = newCity
                        }
                    
                        //3
                        val examModel: StateFlow<List<MidsemScheduleModel>>
                            field = MutableStateFlow(emptyList())  
                        }
                    }    
                    """.trimIndent()
            )

            KotlinCode(
                code = """
                      var uiState = MutableStateFlow(UiState())
                            private set

                      var uiState = MutableStateFlow(UiState()); private set
                            
                      var categories = MutableStateFlow(listOf(CategoryModel("All", "all")))
                            private set
                    """.trimIndent()
            )

            KotlinCode(
                code = """
                    //old way 
                    
                    class CounterViewModel : ViewModel() {
                        //_count → mutable internal state
                        //count → exposed as read-only
                        private val _count = mutableStateOf(0)
                        val count: State<Int> get() = _count

                        fun incrementCount() {
                            _count.value++
                        }
                    }
                    """.trimIndent()
            )

            SpanText(
                text = "To be able to only recomposes components whose data has changed and skips those that are not affected, Compose needs to know what state to track, so that when it receives an update it can schedule the recomposition.",
                modifier = Modifier.padding(topBottom = 0.3.em)
                    .fontSize(FontSize.Medium)
            )

            SpanText(
                text = "Compose keeps track of each composable that reads State value properties and triggers a recomposition when its value changes.",
                modifier = Modifier.padding(topBottom = 0.3.em)
                    .fontSize(FontSize.Medium)
            )

            SpanText(
                text = "Imagine your state as a light switch. Making it observable is like flipping the switch on, indicating to Compose that the state can change and needs attention and be tracked. Compose acts like a motion sensor, detecting any changes in the light switch (state updates) and triggering actions (recomposition) for the affected components (composables).",
                modifier = Modifier.padding(topBottom = 0.3.em)
                    .fontSize(FontSize.Medium)
            )

            SpanText(
                text = "StateFlow and SharedFlow — fixing what was missing\n",
                modifier = Modifier.padding(topBottom = 0.3.em)
                    .fontSize(FontSize.Medium)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Flow itself is cold — it doesn’t hold state between collectors. That’s where StateFlow and SharedFlow come in, and they map almost directly onto the two problems LiveData had.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "StateFlow holds the latest value and replays it to new collectors — perfect for UI state. SharedFlow is for events that shouldn't be replayed: navigation commands, one-off toasts, error triggers. Together, they cover everything SingleLiveEvent was trying to approximate.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            SpanText(
                text = "mutableStateOf function to create an observable MutableState. creates a value that, when changed, tells Compose to redraw.",
                modifier = Modifier.padding(topBottom = 0.3.em)
                    .fontSize(FontSize.Medium)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "State in Compose represents the current data or value that a UI depends on. To use state in Jetpack Compose, we typically declare it as a property within a Composable function. Compose automatically observes state changes and triggers recomposition.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Compose provides various state objects, such as remember, mutableStateOf, and derivedStateOf, to handle different scenarios and ensure proper recomposition when the state changes.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Any value that can change over time is called state, we can take the example of TextEditField here, the user enters values as they want so it changes with respect to the user’s need.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                KotlinCode(
                    code = """
                    fun Counter() {
                        var count by remember { mutableStateOf(0)
                    }
                    """.trimIndent()
                )

                KotlinCode(
                    code = """
                    Button(onClick = { count++ }) {
                            Text(count)
                        }
                    }
                    """.trimIndent()
                )

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "we declare a count state using the mutableStateOf function. Whenever the count value changes, Compose automatically recomposes the UI to reflect the updated value. When the button is clicked, the count value is incremented, triggering recomposition and updating the displayed text.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            SpanText(
                text = "How to read the state within the narrowest possible range",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            KotlinCode(
                code = $$"""
                    //Wrong
                    @Composable
                    private fun Parent() {
                        var count by remember { mutableIntStateOf(0) }
                        // Parent reads state directly → Parent undergoes full recomposition
                        ChildA(count = count)
                    }

                    @Composable
                    private fun ChildA(count: Int) {
                        Text("Count: $count")
                    }
                    
                    //Good
                    @Composable
                    fun Parent() {
                        val count = remember { mutableStateOf(0) }

                        // Pass a lambda that reads the state
                        ChildB(count = { count.value })
                        // Pass without reading the state
                        ChildC(count = count)
                    }

                    @Composable
                    fun ChildB(count: () -> Int) {
                        Text("Count: ${count()}") // Here, the state is actually read.
                    }

                    @Composable
                    fun ChildC(count: State<Int>) {
                        Text("Count: ${count.value}") // Here, the state is actually read.
                    }
                    """.trimIndent()
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Pass the State<T> object itself to the child instead of reading the state directly. If the parent does not call `state.value`, the parent is not traced as reading the state, and only when the child composable needs it does it read `state.value`, triggering a recomposition.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Pass the state access via a lambda. Since lambdas are functions (code, not values), any code inside the lambda that references `state.value` only executes when the lambda is actually called. This delays the state read to the point of invocation, ensuring it’s read only where it’s actually needed.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            SpanText(
                text = "State Management",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "State management using remember and mutableStateOf to remember and update values between recompositions.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Derived states and side effects are managed with derivedStateOf and SideEffect, respectively.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            Ol {
                SpanText(
                    text = "mutableStateOf<T>(...)",
                    modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                        .fontWeight(FontWeight.Bold)
                )

                Ul {
                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "Creates an observable, mutable holder for your state. When you change its .value, Compose is notified.",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }
                }

                SpanText(
                    text = "remember { ... }",
                    modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                        .fontWeight(FontWeight.Bold)
                )

                Ul {
                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "Ensures that the value calculated inside the lambda (in this case, your MutableState) is preserved across re-compositions of the composable.",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }
                }
            }

            SpanText(
                text = "Remember",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "UI-only state (toggling a switch, text field content): use Compose state (mutableStateOf, remember)",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "App state (screen data, loading, error): use ViewModel state (StateFlow, collectAsStateWithLifecycle)",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "One-time events (snackbars, navigation): use SharedFlow / Channel (or a dedicated event wrapper)",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "State that should survive rotation: use rememberSaveable or SavedStateHandle",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Scroll State: rememberScrollState() for Column with verticalScroll() and rememberLazyListState() for LazyColumn",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Animation State: animate*AsState() these return state that changes over time.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            SpanText(
                text = "How can we convert a non-compose state into a Compose state?",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Use remember with mutableStateOf or convert a Flow/LiveData using collectAsState/observeAsState.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Ul {
                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "Wrap your non-Compose state in Compose’s remember and mutableStateOf to create a reactive state that Compose can observe. If you have a non-observable object, you can wrap it in Compose’s mutableStateOf to make it observable.",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }
                }

                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Observing External State with snapshotFlow",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Ul {
                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "If the non-Compose state is a property or a flow, you can use snapshotFlow to observe it reactively in Compose.",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }
                }

                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Adapting LiveData",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Ul {
                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "If the non-Compose state is exposed as LiveData, use the observeAsState() extension function.",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }
                }

                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Adapting Flows or Coroutines",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Ul {
                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "If the state is exposed as a Flow, you can use collectAsState() to adapt it to Compose.",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }
                }

                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Using collectAsState for StateFlow or Flow",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Ul {
                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "collectAsState ensures that changes in the StateFlow or Flow automatically trigger recomposition in the Compose UI.",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }
                }

                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Using observeAsState for LiveData",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Ul {
                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "For LiveData, you can use the observeAsState extension function to convert it into a Compose state and triggers recomposition when the data changes.",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }
                }

                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Custom State Adaptation",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Ul {
                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "For custom non-Compose state objects, create a MutableState manually and synchronize it with the non-Compose state via callbacks or listeners.",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }
                }

                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Wrapping Callbacks into State",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Ul {
                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "If non-Compose state comes from a callback or listener, you can use mutableStateOf or Flow to update Compose state manually.",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }
                }
            }

            SpanText(
                text = "listOf()",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "listOf() creates an immutable List, but at JVM runtime both List and MutableList are represented as java.util.List (the runtime check cannot distinguish between them). For this reason the output is true.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            KotlinCode(
                code = """
                    𝘷𝘢𝘭 𝘭𝘪𝘴𝘵 = 𝘭𝘪𝘴𝘵𝘖𝘧("𝘢𝘱𝘱𝘭𝘦", "𝘣𝘢𝘯𝘢𝘯𝘢")
                    𝘱𝘳𝘪𝘯𝘵𝘭𝘯(𝘭𝘪𝘴𝘵 𝘪𝘴 𝘔𝘶𝘵𝘢𝘣𝘭𝘦𝘓𝘪𝘴𝘵)
                    """.trimIndent()
            )

            SpanText(
                text = "List Vs MutableList",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "List an interface for ArrayList. List provides only read-only properties.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "MutableList inherits from MutableCollection. MutableList features a series of additional methods such as add and remove, granting mutability capacities. MutableCollection inherits from Collection and MutableIterable, which inherit from Iterator. MutableList provides write operations.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            KotlinCode(
                code = """
                    @Suppress("unused")
                    class DirtyList<T>(private val delegate: MutableList<T>, private val callback: (item: T, list: DirtyList<T>) -> Unit = { _, _ ->}) : MutableList<T> by delegate {
                        private var changes: MutableList<T>? = null

                        override fun add(element: T): Boolean {
                            val result = delegate.add(element)
                            trackChange(element)
                            return result
                        }

                        override fun add(index: Int, element: T) {
                            delegate.add(index, element)
                            trackChange(element)
                        }

                        override fun addAll(index: Int, elements: Collection<T>): Boolean {
                            val result = delegate.addAll(index, elements)
                            elements.forEach { trackChange(it) }
                            return result
                        }

                        override fun addAll(elements: Collection<T>): Boolean {
                            val result = delegate.addAll(elements)
                            elements.forEach { trackChange(it) }
                            return result
                        }

                        override fun clear() {
                            delegate.clear()
                            changes = null
                        }

                        override fun remove(element: T): Boolean {
                            val result = delegate.remove(element)
                            if (result) {
                                trackChange(element)
                            }
                            return result
                        }

                        override fun removeAll(elements: Collection<T>): Boolean {
                            val result = delegate.removeAll(elements)
                            elements.forEach { trackChange(it) }
                            return result
                        }

                        override fun iterator(): MutableIterator<T> {
                            return object : MutableIterator<T> {
                                private val iterator = delegate.iterator()
                                private var last: T? = null

                                override fun hasNext(): Boolean {
                                    return iterator.hasNext()
                                }

                                override fun next(): T {
                                    last = iterator.next()
                                    return last!!
                                }

                                override fun remove() {
                                    iterator.remove()
                                    trackChange(last!!)
                                }
                            }
                        }

                        override fun removeAt(index: Int): T {
                            val element = delegate.removeAt(index)
                            trackChange(element)
                            return element
                        }

                        override fun retainAll(elements: Collection<T>): Boolean {
                            val result = delegate.retainAll(elements)
                            changes = delegate.filterNot { elements.contains(it) }.toMutableList()
                            return result
                        }

                        /**
                         * Track a change in the list.
                         */
                        private fun trackChange(element: T) {
                            if (changes == null) {
                                changes = mutableListOf()
                            }
                            changes!!.add(element)
                            callback(element, this)
                        }

                        /**
                         * Get the changes that have been tracked.
                         */
                        fun getChanges(): List<T> {
                            return changes ?: emptyList()
                        }

                        /**
                         * Clear the changes that have been tracked.
                         */
                        fun clearChanges() {
                            changes = null
                        }
                    }

                    /**
                     * Track changes in a mutable list.
                     */
                    fun <T> MutableList<T>.trackChanges(callback: (item: T, list: DirtyList<T>) -> Unit = {_, _ ->}): DirtyList<T> {
                        return DirtyList(this, callback)
                    }
                    """.trimIndent()
            )

            KotlinCode(
                code = """
                    /**
                     * A mutable map that tracks changes.
                     */
                    class DirtyMap<K, V>(
                        private val delegate: MutableMap<K, V>,
                        private val callback: (key: K, value: V, map: DirtyMap<K, V>) -> Unit = { _, _, _ -> }
                    ) : MutableMap<K, V> by delegate {
                        private var changes: MutableMap<K,V>? = null

                        override fun put(key: K, value: V): V? {
                            val oldValue = delegate.put(key, value)
                            trackChange(key, oldValue, value)
                            return oldValue
                        }

                        override fun putAll(from: Map<out K, V>) {
                            delegate.putAll(from)
                            from.forEach { (key, value) -> trackChange(key, null, value) }
                        }

                        override fun remove(key: K): V? {
                            val oldValue = delegate.remove(key)
                            if (oldValue != null) {
                                trackRemovalChange(key, oldValue)
                            }
                            return oldValue
                        }

                        fun trackChange(key: K, @Suppress("UNUSED_PARAMETER") oldValue: V?, newValue: V) {
                            if (changes == null) {
                                changes = mutableMapOf()
                            }
                            changes!![key] = newValue
                            callback(key, newValue, this)
                        }

                        /**
                         * Track the removal of a key-value pair.
                         *
                         * @param key The key.
                         * @param oldValue The old value.
                         */
                        fun trackRemovalChange(key: K, oldValue: V) {
                            callback(key, oldValue, this)
                        }

                        /**
                         * Get the changes.
                         *
                         * @return The changes.
                         */
                        fun getChanges(): Map<K, V> {
                            return changes ?: emptyMap()
                        }

                        /**
                         * Clear the changes.
                         */
                        fun clearChanges() {
                            changes = null
                        }
                    }

                    /**
                     * Track changes in a mutable map.
                     *
                     * @param callback The callback to call when a change is tracked.
                     * @return The dirty map.
                     */
                    fun <K, V> MutableMap<K, V>.trackChanges(callback: (key: K, value: V, map: DirtyMap<K, V>) -> Unit = { _, _, _ ->}): DirtyMap<K, V> {
                        return DirtyMap(this, callback)
                    }
                    """.trimIndent()
            )

            SpanText(
                text = "Reading and Writing State",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "State is read when composables access it during recomposition, and written when state changes (e.g., when you modify a mutableStateOf value). The snapshot system ensures that state updates are batched and applied all at once, avoiding UI flickers or partial updates.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Write operations are enclosed in transactions that bundle state changes together, allowing Compose to apply them atomically when the transaction is committed. This reduces overhead and ensures a smooth user experience.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            SpanText(
                text = "Update Android views and Compose",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Traditional View: These state objects hold the current values of variables that affect the appearance and behavior of UI components. ",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Declarative View: state is often managed externally, Compose encourages developers to manage state directly within the UI components themselves. This approach simplifies state management and leads to more predictable and maintainable UI code.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            SpanText(
                text = "What is a State, Event and how does compose update UI?",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Scenario: Picture a screen with button “Add one”. Every time button is clicked, counter increments (var count) and displayed on the UI as shown below.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Event",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                            .fontWeight(FontWeight.Bold)
                    )
                }

                Ul {
                    Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "User clicking on the button is an event in this scenario. Event can be defined as any input generated by the user or any part of the program, inside or outside the application (like sensors, endpoints, etc).",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Update State",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                            .fontWeight(FontWeight.Bold)
                    )
                }

                Ul {
                    Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "On user click event, count increases. As a result state of count changes.",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }

                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "User click event will awaken the onClick listener of button composable. onClick takes a block of commands as an input [() -> Unit]. Here the block {count+=1} tells what needs to happen when button is clicked.",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Update UI",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                            .fontWeight(FontWeight.Bold)
                    )
                }

                Ul {
                    Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "UI to recompose, count should be an observable variable. This will recompose all composable’s using count when count state changes. We will look at how to make a variable observable in the next section.",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }
                }
            }

            SpanText(
                text = "State",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "State is data that the UI depends on. If the state changes over time and cause UI recomposition, the UI should update automatically.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "State in Jetpack Compose represents data that can change over time and that Compose uses to update the UI when it changes.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "It is a container that holds our data. Whenever there are any changes to this data, it automatically updates all the UI elements that are connected to it. If you need to update your widget data while your app is running, you can use the state object. (Tracks the state of a value. The UI re-composes based on changes to this state.)",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "The state is an object that is connected/subscribed to one or more widgets, contains data, and eager to update the widgets from that data. If there’s any change happens in data, it notifies all widgets to whom it’s connected. The values of the state are changed at runtime.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Jetpack compose has some functional change, which states that the subscribed @composable function(s) recomposes itself with the new data when the state/object value is updated and doesn’t affect or update the whole UI.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "There are two primary ways to manage state inside ViewModel and Composables:",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Ol {
                    Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "mutableStateOf (State from Compose runtime)",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )

                        Ul {
                            Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                                SpanText(
                                    text = "Triggers recomposition when the value changes.",
                                    modifier = Modifier
                                        .fontSize(FontSize.Medium)
                                )
                            }

                            Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                                SpanText(
                                    text = "Lives within Composable memory (remember). remember can be used to store both mutable and immutable objects.",
                                    modifier = Modifier
                                        .fontSize(FontSize.Medium)
                                )
                            }

                            Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                                SpanText(
                                    text = "Doesn’t survive beyond the Composable scope.",
                                    modifier = Modifier
                                        .fontSize(FontSize.Medium)
                                )
                            }

                            Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                                SpanText(
                                    text = "A common pitfall when using mutableStateOf directly inside a ViewModel without encapsulation (i.e., exposing it as a public var) is violating the principle of encapsulation by allowing external classes (like Composable functions) to modify the state directly.",
                                    modifier = Modifier
                                        .fontSize(FontSize.Medium)
                                )
                            }
                        }
                    }

                    KotlinCode(
                        code = """
                             ❌ Exposing mutable state to the UI
                             class MyViewModel : ViewModel() {
                                 var uiState = mutableStateOf("")
                             }
                             
                             ✅ Correct Approach (Encapsulation)
                             class MyViewModel : ViewModel() {
                                 private val _uiState = mutableStateOf("")
                                 val uiState: State<String> = _uiState

                                 fun updateValue(newValue: String) {
                                     _uiState.value = newValue
                                 }
                             }
                        """.trimIndent()
                    )

                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "MutableStateFlow (State from Kotlin's Flow API)",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )

                        Ul {
                            Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                                SpanText(
                                    text = "Lives in ViewModel or business layer.",
                                    modifier = Modifier
                                        .fontSize(FontSize.Medium)
                                )
                            }

                            Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                                SpanText(
                                    text = "Can be collected inside Compose with collectAsstate() or collectAsStateWithLifecycle, because it also collects values from a Flow and transforms it into Compose State.",
                                    modifier = Modifier
                                        .fontSize(FontSize.Medium)
                                )
                            }

                            Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                                SpanText(
                                    text = "Designed for cross-layer shared state.",
                                    modifier = Modifier
                                        .fontSize(FontSize.Medium)
                                )
                            }
                        }
                    }
                }
            }

            SpanText(
                text = "State in Compose",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "In Compose, any state that is read by a composable function should be backed by a special state object returned by functions like these:",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Ol {
                    Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "mutableStateOf / MutableState",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }

                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "mutableStateListOf / SnapshotStateList: A mutableStateListOf() not only stores data but also notifies the UI whenever the list changes. /mutableStateListOf() returns a SnapshotStateList<T>. It behaves like a MutableList<T> but with observable powers.",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }

                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "mutableStateListOf / mutableStateMapOf: Collections That Trigger UI Updates. Use Compose-aware collections when you want in-place changes to trigger recomposition.",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }

                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "mutableStateMapOf / SnapshotStateMap",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }

                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "derivedStateOf",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }

                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "rememberUpdatedState",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }

                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "collect*AsState",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }
                }
            }

            KotlinCode(
                code = """
                        val count = remember { mutableStateOf(0) }
                        val state = remember { mutableStateListOf("A", "B", "C") }
                    """.trimIndent()
            )

            SpanText(
                text = "Mutating a List Instead of Replacing It",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Compose decides whether to recompose by checking if the value of the state changed. Here, the value is a reference to a list, and that reference is exactly the same before and after .add(). The list got longer, but as far as Compose can tell, nothing moved, so the new task never appears.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            KotlinCode(
                code = """
                    @Composable
                    fun TodoList() {
                        val items = remember { mutableStateOf(mutableListOf<String>()) }

                        Column {
                            Button(onClick = { items.value.add("New task") }) {
                                Text("Add")
                            }
                            items.value.forEach { Text(it) }
                        }
                    }
                    """.trimIndent()
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Now, adding, removing, or clearing items all trigger a recomposition on their own. If you’d rather keep a plain list, the other option is to assign a whole new list (items.value = items.value + \"New task\"), which gives Compose a different reference to notice. Either of them works, but mutableStateListOf feels smoother to think about.",
                        modifier = Modifier.fontSize(FontSize.Medium)
                    )
                }
            }

            KotlinCode(
                code = """
                    @Composable
                    fun TodoList() {
                        val items = remember { mutableStateListOf<String>() }

                        Column {
                            Button(onClick = { items.add("New task") }) {
                                Text("Add")
                            }
                            items.forEach { Text(it) }
                        }
                    }
                    """.trimIndent()
            )

            SpanText(
                text = "Compose Is State-Driven",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "State owns the UI",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "UI is a function of state",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "When state changes → UI recomposes automatically",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            SpanText(
                text = "Why State is Important in Compose",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Compose uses state-driven UI, meaning that when state changes, the UI automatically reflects the updated state. This is achieved through recomposition.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Ul {
                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "State drives UI: The UI reflects the current state. This means you do not tell the Ul how to update. You just update the state, and Compose handles the rest.",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }

                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "Recomposition: When the state changes, Compose automatically re-renders the parts of the UI that are dependent on that state. We will cover this in more details in upcoming blog, stay tuned.",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }
                }
            }

            SpanText(
                text = "Manage state",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Use LiveData, StateFlow, ViewModel, Compose State (remember, StateFlow, or rememberSaveable).",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Managing state in Jetpack Compose differs from the traditional Android approach. In Compose, we don't tell views what to do; instead, we describe how the UI should look in a given state. It's like creating a picture of our user interface, and Compose ensures that this picture is always up to date.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "The primary tool for managing state in Compose is the State object. The value stored in a State object can be read by a composable function, and when that value changes, Compose automatically triggers recomposition to reflect those changes in the user interface.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "That's why in Jetpack Compose, we often talk about lifting state up. This means that state that affects multiple composables is stored in one place, typically in the composable higher up in the hierarchy. This way, state changes are propagated to all the composables that depend on that state, and Compose takes care of making those changes visible to the user.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "For example, consider a movie list in a movie browsing application. The state in this case would be the list of movies. This list is \"lifted up\" to the composable responsible for displaying the entire screen. When the movie list changes (e.g., when the user adds a new movie), Compose triggers recomposition to make those changes visible on the screen.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "In Jetpack Compose, state management is not limited to just State objects. We can also leverage tools provided by the Kotlin Coroutines library, such as StateFlow and SharedFlow.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "StateFlow is a type of Flow that represents state. Each new value emitted by a StateFlow triggers recomposition in the composable functions that observe it. This is especially useful when dealing with complex state that is used by multiple different composables.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "For example, if we have application state represented as StateFlow in a ViewModel, we can observe it in a composable function using the collectAsState() function. When the value of the StateFlow changes, Compose automatically triggers recomposition, allowing for immediate reflection of state changes in the user interface.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            SpanText(
                text = "Understanding State",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "State in Compose refers to any data that can change over time and affects the UI.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Ul {
                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "User input (e.g., text entered in a text field)",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }

                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "Values calculated based on other data",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }

                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "Results of asynchronous operations",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }

                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "UI state (e.g., selected item in a list)",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }
                }
            }

            SpanText(
                text = "What is state?",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "A state is an object it can hold our data. If data changes happen, it will update all its subscribed UI widgets. If you want to update the data at runtime in your widgets, you can use the state object.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "The @composable function(s) recomposes itself with the new data when the state/object value is updated and doesn't affect or update the whole UI.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "State function is used to create a mutable state that can be observed, and when the state changes, the composable is recomposed. A state can be read and modified within a composable function.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "A state is a value that affects changes, the UI will update to reflect these changes. To make compose recognize state changes, you need to wrap your state values in a state object.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Whenever the composable is listening to a snapshot state, any change in the state will be reflected by the composable.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Changes in the value of a snapshot state propagate as follows:",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Ul {
                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "Compose goes up the UI tree to find the closest common affected scope (restart scope) and triggers re-execution of the composables.",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }

                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "Re-execute the composables affected by the change in state value (recomposition)",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }
                }

                WebImage(
                    src = Resources.Images.STATE_COMPOSE_1,
                    modifier = Modifier.fillMaxWidth().padding(5.px).borderRadius(10.px),
                    contentDescription = "State compose"
                )
            }

            SpanText(
                text = "Types of states",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(top = 1.em).toAttrs()) {
                    SpanText(
                        text = "Screen UI State (ViewModel)",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                            .fontWeight(FontWeight.Bold)
                    )
                }

                Ul {
                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "Information to present to the user (e.g., data, messages). This is basically what you need to display on the screen.",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }

                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "Let’s say you have a list that you obtain data from an API and show to the user. When the lists change, the UI also needs to be updated.",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }

                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "This logic is not relevant to UI components.",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }

                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "We refer to this kind of logic as the screen UI state. It is typically connected with other layers of the hierarchy because it contains app data.",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }

                    Ul {
                        Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                            SpanText(
                                text = "In Jetpack Compose, the screen UI state is determined by applying business rules.",
                                modifier = Modifier
                                    .fontSize(FontSize.Medium)
                            )
                        }

                        Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                            SpanText(
                                text = "To manage this state, it is common practice to hoist the screen UI state to a screen-level state holder, often implemented as a ViewModel.",
                                modifier = Modifier
                                    .fontSize(FontSize.Medium)
                            )
                        }

                        Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                            SpanText(
                                text = "By hoisting the state to the ViewModel, it allows for centralized management and coordination of the screen’s UI state, ensuring consistency and separation of concerns in the application architecture.",
                                modifier = Modifier
                                    .fontSize(FontSize.Medium)
                            )
                        }

                        Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                            SpanText(
                                text = "We have handled all the situations that may occur on the screen in the ViewModel class. This state is connected with other layers of the hierarchy and contains app data to be displayed to the user.",
                                modifier = Modifier
                                    .fontSize(FontSize.Medium)
                            )
                        }
                    }
                }

                Li(Modifier.padding(top = 1.em).toAttrs()) {
                    SpanText(
                        text = "UI element state (User interface)",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                            .fontWeight(FontWeight.Bold)
                    )
                }

                Ul {
                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "The UI element state is managed externally to the composable and can be moved to the calling composable function or a state holder. Internal state of UI elements (e.g., expanded state, scroll position).",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }

                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "Unlike Android Views, where the state is managed internally, Jetpack Compose treats state as separate from the composable.",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }

                    Ul {
                        Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                            SpanText(
                                text = "The UI element state refers to characteristics of UI elements that affect how they look and behave.",
                                modifier = Modifier
                                    .fontSize(FontSize.Medium)
                            )
                        }

                        Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                            SpanText(
                                text = " For example, a UI element can be shown or hidden, have a specific font, font size, or font color. Also, the state is stored separately from the UI element itself.",
                                modifier = Modifier
                                    .fontSize(FontSize.Medium)
                            )
                        }

                        Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                            SpanText(
                                text = "It can be placed in the surrounding code or a designated state holder. This allows for more flexibility and control over how the UI elements behave and appear.",
                                modifier = Modifier
                                    .fontSize(FontSize.Medium)
                            )
                        }
                    }
                }
            }

            SpanText(
                text = "Why use states",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "States are a powerful feature of Jetpack Compose that allow developers to create dynamic and interactive UIs with minimal code. By using states, we can update the UI in response to user interactions or changes in data, without having to manage complex UI components manually.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "In Compose, state can be managed using the remember function, which creates a variable that survives recomposition, and the mutableStateOf() function, which creates a mutable state object that can be updated. The state can be accessed using the value property or using a property delegate such as var state by remember { mutableStateOf() }.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            SpanText(
                text = "How does Jetpack Compose handle UI state changes?",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "In Jetpack Compose, UI state changes are managed through events. When a user interacts with a UI element, such as clicking a button, an event is emitted, and the corresponding event handler can update the UI state accordingly.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            SpanText(
                text = "Compose State and StateFlow",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 1.em).toAttrs()) {
                    SpanText(
                        text = "Compose State – We only need a single variable. To avoid exposing a mutable property to our Compose code, we can add a private setter. Updating the state is a straightforward assignment expression.  Access the variable directly from the ViewModel. It is automatically unwrapped because of the delegate (by keyword) used in the ViewModel.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 1.em).toAttrs()) {
                    SpanText(
                        text = "StateFlow – Here, we need two variables, a private mutable one and a public immutable one. We call this a backing property, and its purpose is to allow us to expose an immutable version of our state, which is backed by a mutable version that we can more easily work with. Updating the state is also a simple one-liner. To safely collect the Flow by adhering to the lifecycle of the composable, we use the collectAsStateWithLifecycle() function. This will manage the cancellation and restart of the Flow collection based on the LifeCycleOwner’s current state. Just like with composeColor in the ViewModel, we will now use delegation in our MainScreen composable to handle unwrapping the color value that we can access directly with flowState instead of needing to call flowState.value.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            SpanText(
                text = "Managing State in Compose (Declare a MutableState object)",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            KotlinCode(
                code = """
                    //How to create a state with remember API let's see the below syntax

                    val mutableValue = remember { mutableStateOf(default) }
                    var value by remember { mutableStateOf(default) }
                    val (value, setValue) = remember { mutableStateOf(default) }
                    """.trimIndent()
            )

            Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                SpanText(
                    text = "if you see in the above example we have a total 3 ways to create a state with remember API and “mutableStateOf” The “mutableStateOf” is kind of observerable with can read and modified which triggers the recomposition of the composable to recompose. let me explain the difference between all this syntax.",
                    modifier = Modifier
                        .fontSize(FontSize.Medium)
                )
            }

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "val value = remember { mutableStateOf(default) }",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                            .fontWeight(FontWeight.Bold)
                    )
                }

                Ul {
                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "you can read and modify the value by using the “value” property like “mutableValue.value” to read and if you want to modify then “mutableValue.value = <YOUR_VALUE>”.",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }
                }

                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "var value by remember { mutableStateOf(default) }",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                            .fontWeight(FontWeight.Bold)
                    )
                }

                Ul {
                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "As you can see we have the new keyword “by” which delegates the property to the state holder created by “mutableStateOf”, this will help you to use “value” as a normal kotlin variable.",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }
                }

                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "val (value, setValue) = remember { mutableStateOf(default) }",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                            .fontWeight(FontWeight.Bold)
                    )
                }

                Ul {
                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "You can see we have “value” which is the getter and “setValue” is a setter which means if you want to use the state you can use “value” and for the modification you can use “setValue”.",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }
                }
            }

            KotlinCode(
                code = """
                    val randomNumber = remember { (0..100).random() }
                    """.trimIndent()
            )

            SpanText(
                text = "State Management Techniques",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ol {
                Li(Modifier.padding(top = 1.em).toAttrs()) {
                    SpanText(
                        text = "StateFlow",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Ul {
                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = " If your application state changes across multiple components, implement `StateFlow` for managing state changes seamlessly, maintaining consistency across the board.",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }

                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "This is a reactive stream that emits new values whenever the state changes.",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }

                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "It offers better performance and flexibility than MutableState for complex state management.",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }

                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "The observable stream exposed by the ViewModel.",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }

                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "If you’re building real screens, you want screen state in the ViewModel.",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }

                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "StateFlow has a thread-safe update function. This allows you to safely update your states from any coroutine (even if it is not running on the main dispatcher). Thread safety is vital for avoiding race conditions when working with asynchronous code, so having a built-in way to achieve this is an excellent advantage.",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }

                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "StateFlow, we have access to all the intermediary Flow operators like map, filter, onEach and combine.",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }

                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "Compose state can be turned into a StateFlow using the snapshotFlow {} function and the stateIn() call. You can even turn part of your Compose state into a StateFlow. With this: snapShotFlow { state.username } (along with stateIn() you will have a StateFlow of a username value.",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }
                }

                KotlinCode(
                    code = $$"""
                        sealed interface WeatherUiState {
                            data object Idle : WeatherUiState
                            data object Loading : WeatherUiState
                            data class Success(val city: String, val tempC: Double) : WeatherUiState
                            data class Error(val message: String) : WeatherUiState
                        }

                        class WeatherViewModel : ViewModel() {
                            private val _uiState = MutableStateFlow<WeatherUiState>(WeatherUiState.Idle)
                            val uiState: StateFlow<WeatherUiState> = _uiState
                            fun load(city: String) {
                                viewModelScope.launch {
                                    _uiState.value = WeatherUiState.Loading
                                    runCatching {
                                        // pretend repo call
                                        delay(800)
                                        22.5
                                    }.onSuccess { temp ->
                                        _uiState.value = WeatherUiState.Success(city, temp)
                                    }.onFailure { e ->
                                        _uiState.value = WeatherUiState.Error(e.message ?: "Unknown error")
                                    }
                                }
                            }
                        }
                        
                        @Composable
                        fun WeatherScreen(viewModel: WeatherViewModel) {
                            val uiState by viewModel.uiState.collectAsStateWithLifecycle()

                        when (val s = uiState) {
                                WeatherUiState.Idle -> Text("Type a city")
                                WeatherUiState.Loading -> CircularProgressIndicator()
                                is WeatherUiState.Success -> Text("${s.city}: ${s.tempC}°C")
                                is WeatherUiState.Error -> Text("Error: ${s.message}")
                            }
                        }
                    """.trimIndent()
                )

                Li(Modifier.padding(top = 1.em).toAttrs()) {
                    SpanText(
                        text = "ViewModel",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Ul {
                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "This is a class that holds and manages data for a specific screen in your app. It helps to separate UI logic from business logic and ensures data consistency across different composable functions.",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }
                }

                Li(Modifier.padding(top = 1.em).toAttrs()) {
                    SpanText(
                        text = "LiveData",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Ul {
                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "While not directly supported by Compose, LiveData can still be used in combination with Compose for state management. It provides a way to observe changes in data and update the UI accordingly.",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }
                }

                Li(Modifier.padding(top = 1.em).toAttrs()) {
                    SpanText(
                        text = "MutableState",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Ul {
                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "This is the most basic state management technique in Compose. It allows you to store a single mutable variable and update it whenever needed.",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }

                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "Jetpack Compose provides the MutableState interface.",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }

                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "Holding mutable data",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }

                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "Compose automatically triggers a UI recomposition when the data inside a MutableState changes, ensuring that the UI reflects the updated state.",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }
                }

                Li(Modifier.padding(top = 1.em).toAttrs()) {
                    SpanText(
                        text = "remember",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Ul {
                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "This function allows you to store state that survives recompositions within a composable function. A composable that uses remember to store an object creates internal state, making the composable Stateful.",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }

                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "The remember function ensures that this state is kept across recompositions of the UI. It means that even if the UI gets refreshed, candyCount retains its value.",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }

                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "candyCount: This is the variable name. It acts like the label on our smart box, allowing us to easily access and update its contents.",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }
                }

                Li(Modifier.padding(top = 1.em).toAttrs()) {
                    SpanText(
                        text = "State Hoisting",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(top = 1.em).toAttrs()) {
                    SpanText(
                        text = "Side effect / Effect Handler",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(top = 1.em).toAttrs()) {
                    SpanText(
                        text = "compositionLocal",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Ul {
                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "This function allows you to share state between composable functions within a specific subtree of the UI hierarchy.",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }
                }
            }

            SpanText(
                text = "Why use StateFlow?",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "In Jetpack Compose, state can be managed directly within composables using functions like remember and mutableStateOf. While this approach is fine for simpler scenarios, it has limitations when it comes to more complex apps. For example, if your app’s state needs to persist across configuration changes or be shared between multiple composables, relying solely on composable state can lead to complications.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "A state-holder observable flow that emits the current and new state updates to its collectors.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            SpanText(
                text = "MutableState",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "State",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Ul {
                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "This is a read-only interface that represents the current value of a state variable. It has a single property value, which provides access to the current state. It doesn’t have the capability to change the value itself. This data can change over time based on user interactions, network responses, or other events.",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }
                }

                Ul {
                    Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "The MutableState subclass allows both reading and modifying the state.",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }
                }

                KotlinCode(
                    code = $$"""
                    @Composable
                    fun DisplayCounterState(state: State<Int>) {
                        // The state is read-only here, we can't modify it directly
                        Text("Counter value: ${state.value}")
                    }

                    @Composable
                    fun DisplayCounterLambda (count: () -> Int) {
                        Text "Count: ${count()}") // Here, the state is actually read.
                    }
                    
                    @Composable
                    fun CounterApp() {
                        var count by mutableStateOf(0)
                        DisplayCounterState(state = remember { mutableStateOf(count) })  // Passing State as read-only to child
                    
                        // Pass a lambda that reads the state
                        DisplayCounterLambda(count = { count.value })
                        
                        Button(onClick = { count++ }) {
                            Text("Clicked $count times")
                        }
                    }
                    """.trimIndent()
                )

                Li(Modifier.padding(top = 1.em).toAttrs()) {
                    SpanText(
                        text = "MutableState",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Ul {
                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "Lives within Composable memory (remember).",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }

                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "Lives within Composable memory (remember). Doesn’t survive beyond the Composable scope.",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }

                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "This interface inherits from State and provides the ability to both read and modify the state value. Importantly, it's the mutability aspect that enables observability.",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }

                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "Return a new MutableState initialized with the passed in value. The MutableState class is a single value holder whose reads and writes are observed by Compose. Additionally, writes to it are transacted as part of the Snapshot system.",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }

                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "When a MutableState object's value changes, the system triggers a recomposition of all the composables that depend on this state.",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }

                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "Compose utilizes a recomposition mechanism, where only the composables that depend on the changed state are updated, rather than the entire UI tree. This selective recomposition improves performance and responsiveness.",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }

                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "Using MutableState is straightforward — developers can declare state variables using the mutableStateOf function provided by Compose, simplifying application state management.",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }
                }
            }

            KotlinCode(
                code = """
                    val count = remember { mutableStateOf(0) }

                    // altering value
                    count.value = 10
                    """.trimIndent()
            )

            SpanText(
                text = "How does State trigger Recomposition in Jetpack Compose?",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "When you declare a mutableStateOf for something like a counter, Compose internally turns it into a SnapshotMutableState. This object doesn’t just store the value—it also keeps a snapshotId that represents the current change generation, and it builds a dependency list of all Composables that read this value during Composition. In other words, the state becomes “observable,” with precise tracking of who depends on it.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Whenever a Composable reads the state, registration happens automatically: during Composition, the Runtime adds that Composable as a dependent inside the SnapshotMutableState. No extra API calls are needed—just reading the value establishes the link. This creates a clear mapping between a given piece of state and the Composables influenced by it.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "When the user changes the value—for example counter++—the SnapshotMutableState updates the value, marks that a write happened in the current snapshot, and then notifies the Compose Runtime that Recomposition is required for the registered dependents. The key point here is that the signal is targeted: instead of “rebuilding the whole screen,” only the Composables that actually read the changed value are scheduled for recomposition.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "That’s where the Slot Table comes in. The Slot Table is the internal representation of the Composition tree—it holds the nodes (composables), their order, and the parameters they were last built with. The Runtime uses the dependency list to know which Composables to re-execute, then consults the Slot Table to find their exact locations. From there it decides: if the parameters haven’t changed, it performs a skip and reuses the node; if they have, it performs a re-run of the Composable and updates its record in the Slot Table. This makes the process selective and efficient instead of being a full rebuild of the tree.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "The result is that Compose only recomposes the affected branches while skipping the rest. That has a direct impact on performance and smoothness. Once you understand this lifecycle—mutableStateOf → SnapshotMutableState with value, snapshotId, and dependents → Runtime notification → Slot Table–guided skip or re-run—you can see why Recomposition in Compose is fast, predictable, and doesn’t require manual management.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            SpanText(
                text = "MutableState work Compose",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "MutableState is an observable data holder that allows composables to react to changes in state automatically. When the value of a MutableState object changes, Jetpack Compose triggers a recomposition for any composables that read that state, updating the UI to reflect the new data.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "State is managed via a Single Source of Truth (SSOT) (e.g. mutableStateOf().",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "MutableState is typically created using the mutableStateOf function. This function returns an instance of MutableState that holds the initial value and updates the value whenever it changes.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            KotlinCode(
                code = """
                    @Composable
                    fun Counter() {
                        var count by remember { mutableStateOf(0) }
                        Button(onClick = { count++ }) {
                            Text("Clicked $\count times")
                        }
                    }
                    """.trimIndent()
            )

            SpanText(
                text = "MutableState with Backing Properties in ViewModel",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "When working within a ViewModel in Jetpack Compose, MutableState can effectively be used with backing properties to encapsulate and manage the UI-related state.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "However, while MutableState is similar in some respects to MutableStateFlow, there are critical differences, especially in encapsulation and exposure.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Backing property - A backing property allows you to provide a controlled way of accessing and updating your state. It is a common practice in Kotlin to use private mutable state internally in a class (like a ViewModel) while exposing an immutable or read-only version of that state to the outside world.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "In this example, _count is a private MutableState<Int> which is not directly accessible from outside the ViewModel. Instead, the ViewModel exposes count, a read-only State<Int>. This pattern helps to preserve encapsulation and ensures that the state can only be modified through defined ViewModel methods like incrementCount().",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

            }

            KotlinCode(
                code = """
                    class CounterViewModel : ViewModel() {
                        //_count → mutable internal state
                        //count → exposed as read-only
                        private val _count = mutableStateOf(0)
                        val count: State<Int> get() = _count

                        fun incrementCount() {
                            _count.value++
                        }
                    }
                    """.trimIndent()
            )

            SpanText(
                text = "create state object",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "This cryptic line of code is what is used in composable functions to represent state. It might look really confusing at first, so let's break down what is happening here:",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Ul {
                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "mutableStateOf(true) will produce a MutableState<Boolean> object which will hold the value of our state (which is true).",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }

                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "remember{} tells Compose that it will need to remember the value passed to it, so that it does not execute the lambda on every recomposition.",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }

                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "by is a Kotlin keyword for delegates. It hides the fact that the mutableStateOf() returns a MutableState object and it allows us to use enabled as if it was a boolean instead.",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }
                }
            }

            SpanText(
                text = "How snapshot state delegation and destructuring works?",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            //https://mail.google.com/mail/u/0/?tab=rm&ogbl#search/recomposition/FMfcgzQfBslFPRRkkMTLZFBgbqxcSLrW
            KotlinCode(
                code = """
                    val state1 = remember { mutableStateOf( value = "") } 
                    TextField(value = state1.value, onValueChange = { state1. value = it })
            
                    var state2 by remember { mutableStateOf( value = "") }
                    //TextField(value = state2,  onValueChange = { newValue -> state2 = newValue })
                    TextField(value = state2, onValueChange = { state2 = it }) //Short Syntax
            
                    val (value3, setValue3) = remember { mutableStateOf( value = "") }
                    TextField(value = value3, onValueChange = setValue3)
                    
                    //New
                    var searchText by remember { mutableStateOf(TextFieldValue("")) }
                    """.trimIndent()
            )

            Ol {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "The first approach is straightforward: mutableStateOf creates snapshot state instance that we use to store state. Snapshot state guarantees component recomposition when state changes, remember persists the same state object across recompositions. This is the most rudimentary way to store local state, but there are two ways to simplify this state reading or modification.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "The second approach uses property delegation. When we use by keyword after a variable, all reads of this variable are just a call of getValue on the object on the right, and all writes are calls of setValue of this object. In consequence, reading and writing state is easier, and the behavior is the same as in the first approach.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "The third approach isn’t popular in Compose. I believe it was inspired by how is state defined in React. It is MutableState destructuring into value and setter. This regular bracket is used for position-based destructuring, that just calls componentN (where N is position index) for each variable inside the bracket. MutableState provides component1 that just calls value, and component2 that creates a setter.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            //https://github.com/pbclive-digital/pbc-web-app.git (contentText)
            KotlinCode(
                code = """
                    //TextFieldValue Search/Filter

                    @Composable
                    @Preview
                    fun App() {
                        MaterialTheme {
                            var searchText by remember { mutableStateOf(TextFieldValue("")) }

                            val commands = listOf(
                                "Open App",
                                "Search Files",
                                "View System Status",
                                "Run Command",
                                "Settings"
                            )

                            Column(
                                modifier = Modifier
                                    .fillMaxSize()
                                    .padding(16.dp)
                            ) {
                                SearchBox(searchText) { searchText = it }
                                CommandList(commands.filter { it.contains(searchText.text, ignoreCase = true) })
                            }
                        }
                    }
                    
                    /*
                        val firstName = remember { mutableStateOf(TextFieldValue()) }
                        contentText = firstName
                    */

                    @Composable
                    fun SearchBox(
                        //contentText: MutableState<TextFieldValue>,
                        searchText: TextFieldValue, 
                        onSearchTextChanged: ((newValue: TextFieldValue) -> Unit)? = null) 
                     {
                        OutlinedTextField(
                            value = searchText,
                            //value = contentText.value,
                            onValueChange = { newValue ->
                                onValueChange?.invoke(newValue)
                            },
                            label = { Text("Search") },
                            modifier = Modifier
                                .fillMaxWidth()
                                .background(Color(0xFF2C2C2C))
                                .padding(16.dp),
                            singleLine = true,
                            textStyle = MaterialTheme.typography.h6.copy(color = Color.White),
                            colors = TextFieldDefaults.outlinedTextFieldColors(
                                backgroundColor = Color(0xFF2C2C2C),
                                focusedBorderColor = Color(0xFF4CAF50),
                                unfocusedBorderColor = Color.Gray
                            )
                        )
                    }

                    @Composable
                    fun CommandList(commands: List<String>) {
                        val scrollState = rememberScrollState()

                        Column(modifier = Modifier.verticalScroll(scrollState)) {
                            commands.forEach { command ->
                                CommandItem(command)
                                Spacer(modifier = Modifier.height(10.dp))
                            }
                        }
                    }

                    @Composable
                    fun CommandItem(command: String) {
                        Card(
                            modifier = Modifier
                                .fillMaxWidth()
                                .padding(8.dp)
                                .background(Color(0xFF333333)),
                            elevation = 4.dp
                        ) {
                            Row(
                                modifier = Modifier
                                    .fillMaxWidth()
                                    .padding(16.dp),
                                verticalAlignment = Alignment.CenterVertically
                            ) {
                                Text(command, style = MaterialTheme.typography.body1, color = Color.Black)
                            }
                        }
                    }
                    """.trimIndent()
            )

            SpanText(
                text = "ViewModel",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            //https://github.com/AhmedOmara14/Kmp-Architecture (TextField Default Value Store, TimeLine ConstraintLayout)
            //https://github.com/thejohnsondev/PassGuard-Multiplatform.git (MutableState)
            KotlinCode(
                code = """
                    //Effective state management for TextField in Compose
                    
                    //Cheap, synchronous validation (For example UserName length check, email pattern check) → derivedStateOf.
                    //Async / expensive validation (For example Check the UserName using Database) → LaunchedEffect / produceState.
                    //Validation requires suspending work (API, DB, delay) / Need debounce/throttle → snapshotFlow
                    
                    class SignUpViewModel(private val signUpRepository: SignUpRepository) : ViewModel() {
                        var username by mutableStateOf("")
                            private set    

                        val userNameHasError: StateFlow<Boolean> =
                            snapshotFlow { username }
                                .mapLatest { signUpRepository.isUsernameAvailable(it) }
                                .stateIn(
                                    scope = viewModelScope,
                                    started = SharingStarted.WhileSubscribed(5_000),
                                    initialValue = false
                            )
            
                        fun updateUsername(input: String) {
                            username.value = input
                        }
                                                
                        val userNameHasLocalError by derivedStateOf { 
                            // synchronous call
                            signUpRepository.isUsernameCorrect(username) 
                        }
                    }
                    
                    //Validation UI
                    //1
                    val userNameHasError by viewModel.userNameHasError.collectAsStateWithLifecycle()
                    if (userNameHasError) {
                        Text(
                            text = "Username not available. Please choose a different one.",
                            color = Color(ColorError)
                        )
                    }
                    
                    //2
                    viewModel.userNameHasLocalError?.let { error ->
                        Text(error, color = Color.Red)
                    }
                    
                    //TextField Default Value Store
                    @Composable
                    fun AppTextInput(
                        modifier: Modifier = Modifier,
                        hint: String? = null,
                        placeholder: String? = null,
                        defaultValue: String? = null,
                        onValueChange: (value: String) -> Unit,
                        errorString: String? = null,
                        imeAction: ImeAction = ImeAction.Next,
                        keyboardType: KeyboardType = KeyboardType.Text,
                        maxLength: Int = Int.MAX_VALUE,
                    ) {
                        var value by rememberSaveable(defaultValue) { mutableStateOf(defaultValue ?: "") }
                       
                        OutlinedTextField(
                            value = value
                            onValueChange = {
                                if (it.length <= maxLength) {
                                    value = it
                                    onValueChange(it)
                                }
                            }
                        )
                    }
                    
                    //MutableState
                    val emailState = rememberSaveable { mutableStateOf(String.empty) }
                    
                    @Composable
                    fun PrimaryOutlinedTextField(
                        textState: MutableState<String>,
                        onTextChanged: (String) -> Unit,
                    ) {
                        OutlinedTextField(
                            value = textState.value,
                            onValueChange = {
                                onTextChanged(it)
                        }
                    }
                    """.trimIndent()
            )

            SpanText(
                text = "asStateFlow()",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li {
                    SpanText(
                        text = "asStateFlow() extension function we put on our state. Using asStateFlow() will make the StateFlow read-only. This may seem a little pointless, we only update our backing property, not the public state, but let’s take a look at what can happen if we don’t do this. Without making the state read-only, you can update the state from anywhere, even the UI.",
                        modifier = Modifier.padding(topBottom = 0.2.em)
                            .fontSize(FontSize.Medium)
                    )
                }

                Li {
                    SpanText(
                        text = "Ensure that your state is only able to be updated from the backing property by using asStateFlow()",
                        modifier = Modifier.padding(topBottom = 0.2.em)
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            SpanText(
                text = "value.copy",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            KotlinCode(
                code = """
                    data class SearchUiState(
                        val query: String = "",
                        val isLoading: Boolean = false,
                        val error: String? = null
                    )
                    
                    private val _state = MutableStateFlow(SearchUiState())
                    val state: StateFlow<SearchUiState> = _state.asStateFlow()
                       
                    _state.value = _state.value.copy(
                        isLoading = false,
                        error = it.message ?: "Search failed"
                    )
                       
                    //Improve Code
                    _state.update { it.copy(isLoading = true, error = null) }
                    
                    //Better Way
                    sealed interface SearchUiState {
                        data object Idle : SearchUiState
                        data object Loading : SearchUiState
                        data class Success(val results: List<String>) : SearchUiState
                        data class Error(val message: String) : SearchUiState
                    }
                    
                    private val _state = MutableStateFlow(SearchUiState())
                    val state = _state.asStateFlow()

                    fun onError(message: String?) {
                        _state.update {
                            it.copy(
                                isLoading = false,
                                error = message ?: "Search failed"
                            )
                        }
                    }
                    """.trimIndent()
            )

            SpanText(
                text = "_state.value = newValue",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "When the new state does not depend on the current state. When you are absolutely certain the update will only happen on a single thread (e.g., exclusively on the main thread in a specific architecture).",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            KotlinCode(
                code = """
                    _state.value.name = "Kamlesh" // mutating object
                    
                    Why not update compose value?
                       StateFlow checks: “Did the value object change?”
                    1. You are assigning a new object   
                    2. Reference didn’t change
                    3. StateFlow thinks it's the same object, only internal field changed
                    4. No emission / recomposition
                    
                    State updates only when reference changes
                    """.trimIndent()
            )

            SpanText(
                text = "updateAndGet()",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li {
                    SpanText(
                        text = "This function is similar to the previous one, but does the opposite. First, you will update your state and then you get the value of the newly updated state. This can be very useful if you need to perform an action on the newly updated state right away.",
                        modifier = Modifier.padding(topBottom = 0.2.em)
                            .fontSize(FontSize.Medium)
                    )
                }

                KotlinCode(
                    code = """
                                    //This now holds the new state
                                    val newState = _state.updateAndGet {
                                        //Updating state here.
                                        it.copy(
                                            text = event.value
                                        )
                                    }
                                """.trimIndent()
                )
            }

            SpanText(
                text = "getAndUpdate()",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li {
                    SpanText(
                        text = "The getAndUpdate will first get the current state, and then you can update it in a lambda. Updates the MutableStateFlow.value atomically using the specified function of its value, and returns its prior value.",
                        modifier = Modifier.padding(topBottom = 0.2.em)
                            .fontSize(FontSize.Medium)
                    )
                }

                KotlinCode(
                    code = """
                                    //oldValue will get the old state.
                                    val oldValue = _state.getAndUpdate {
                                    //In here, we will update the state.
                                        it.copy(
                                            text = event.value
                                        )
                                    }
                                """.trimIndent()
                )

                Li {
                    SpanText(
                        text = "This function can be helpful if you need to perform a function based on the previous state. Maybe you want to log the previous state before updating it.",
                        modifier = Modifier.padding(topBottom = 0.2.em)
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            SpanText(
                text = "Update()",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Use data class with copy() for immutable state updates. update() {} extension method on MutableStateFlow which ensures atomic updates based on the latest state. Thread-safe solution for modifying state that depends on its previous value.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Ul {
                    Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "Keeps other fields (error, etc.) unchanged.",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }

                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "Immutable state pattern.",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }

                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "Recommended for MVI / Compose / StateFlow.",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }
                }
            }

            KotlinCode(
                code = """
                    //These three are exactly the same behavior:
                    
                    //1 Explicit parameter name - Best in large lambdas
                    _uiState.update { uiState -> uiState.copy(count = uiState.count + 1) }
                    
                    //2 Better readability
                    _uiState.update { state ->
                        state.copy(count = state.count + 1)
                    }
        
                    //3 Implicit parameter (it) - Small updates 
                    it = it is the default name of the single lambda parameter. Works only when the lambda has one parameter. current state
                    copy() creates a new instance of the data class
                    Only the specified fields change
                    
                    _uiState.update { it.copy(isLoading = false, books = newBooks) }
                    """.trimIndent()
            )

            SpanText(
                text = "Update() work (Atomicity)",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "The update function internally wraps the Read-Modify-Write operation in a single, atomic block. It uses a compare-and-swap mechanism (or similar concurrency primitives) to ensure that no other thread modifies the value between the moment you read it and the moment you write the new one.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "If the value was modified by another thread during your calculation, the lambda is re-executed with the new current value until the write operation succeeds atomically.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            KotlinCode(
                code = """
                    //Wrong Way
                    state.update {
                        val newData = fetchExpensiveData() // ❌ Expensive and non‑deterministic
                        it.copy(data = newData)
                    }

                    //Good Way
                    val newData = fetchExpensiveData() // ✅ Executed once, outside update

                    state.update {
                        it.copy(data = newData)
                    }

                    //Wrong Way
                    fun doSomeLogicAfterLoading(): Result {
                        state.update { it.copy(isLoading = false) } // ❌ BAD: calls update inside update
                        return doSomeLogic()
                    }

                    state.update {
                        it.copy(data = doSomeLogicAfterLoading()) // ❌ Causes recursion or inconsistency
                    }

                    //Good Way
                    fun doSomeLogicAfterLoading(): Result {
                        return doSomeLogic()
                    }

                    state.update { it.copy(isLoading = false) }

                    val result = doSomeLogicAfterLoading()

                    state.update { it.copy(data = result) }
                    """.trimIndent()
            )

            SpanText(
                text = "MutableState vs. State",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "MutableState: This is a type that holds mutable state, allowing the state to be changed. When the state is updated, Compose automatically re-composes the UI to reflect the changes.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "State: Unlike MutableState, this is an immutable type and is mainly used when exposing state to the UI without the need for modifications.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            SpanText(
                text = "mutableStateOf (State from Compose runtime)",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "mutableStateOf is a function that helps manage state in a composable. mutableStateOf is a function provided by Jetpack Compose that creates a mutable state holder, and when the value changes, it triggers recomposition of any composable that reads it.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "mutableIntStateOf or mutableFloatStateOf for handling primitives more directly.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "It takes an initial value as a parameter and returns an MutableState object that can hold and update that value.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "mutableStateOf and remember { mutableStateOf } are used to declare a state that can be changed and observed. These mutable states allow Compose to “observe” changes and “recompose” (i.e., update) only the parts of the UI that are affected by these changes.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "It initializes a state object with an initial value, retains its state across recompositions, triggers recomposition when the state changes, and provides read and write access to its value. This function enables reactive UI updates based on changes to state variables.", modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "mutableStateOf function, you’ll see that there’s a second parameter with a default value of structuralEqualityPolicy(), which recognizes changes based on the structural changes of your object.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "neverEqualPolicy(), which always considers the old object different from the new object (Always treats new values as different, even if they’re exactly the same). Forces recomposition on every assignment. This can be useful if you want a recomposition happening by just setting the state value.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "referentialEqualityPolicy(), which considers a change if the memory reference of the old object is different from the memory reference of the new one.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "mutableStateOf returns a special type, SnapshotMutableStateImpl, which works with Compose’s snapshot system. That returns a SnapshotMutableStateImpl which: Stores your value. Tracks when it last changed. Uses a policy to decide if the change should trigger recomposition.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "mutableStateOf is a function that creates such a mutable state variable. When a value in a mutable state is modified, all composables that rely on that value are automatically and intelligently updated. This keeps your UI responsive and up-to-date without manual intervention.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "remember { mutableStateOf } is a Composable function that creates a state object and remembers its value. This means that when the state changes, the Composable function will automatically recompose, reflecting the new state.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "In summary, mutableStateOf is a simple way to create a mutable state, while remember { mutableStateOf } is a Composable function that creates a state object that will trigger recomposition when the state changes.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "So, if you need your Composable to be automatically recomposed when the state changes, use remember { mutableStateOf }. Otherwise, mutableStateOf will suffice.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            KotlinCode(
                code = """
                    @StateFactoryMarker
                    fun <T> mutableStateOf(
                        value: T,
                        policy: SnapshotMutationPolicy<T> = structuralEqualityPolicy()
                    ): MutableState<T> = createSnapshotMutableState(value, policy)
                    """.trimIndent()
            )

            Ul{
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "value: T parameter is simply the initial value you want the state to hold. The T is a generic type, which means it can be anything—an Int, a String, a data class, or even a nullable type.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            KotlinCode(
                code = """
                    //Loading 
                    
                    @Composable
                    fun AnimatedLoadingCircle(
                        modifier: Modifier = Modifier,
                        tint: Color = Theme.colors.primary
                    ) {

                        var angle by remember { mutableFloatStateOf(0f) }

                        LaunchedEffect(Unit) {
                            while (true) {
                                angle = (angle + 45f) % 360f
                                delay(100)
                            }
                        }

                        Icon(
                            modifier = modifier.rotate(degrees = angle),
                            imageVector = ImageVector.vectorResource(R.drawable.loading),
                            contentDescription = stringResource(R.string.loading),
                            tint = tint,
                        )
                    }
                    """.trimIndent()
            )

            KotlinCode(
                code = """
                    data class User(val name: String)

                    val defaultState = mutableStateOf(User("Kotlin"), structuralEqualityPolicy())
                    val referentialState = mutableStateOf(User("Kotlin"), referentialEqualityPolicy())
                    val neverEqualState = mutableStateOf(User("Kotlin"), neverEqualPolicy())

                    // New instance, same content
                    val newUser = User("Kotlin")

                    // Default (structural equality)
                    defaultState.value = newUser       // No recomposition (contents equal)

                    // Referential equality
                    referentialState.value = newUser  // Recomposition (different instance)

                    // Never equal
                    neverEqualState.value = newUser  // Recomposition (always triggers)
                    """.trimIndent()
            )

            SpanText(
                text = "When to use",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Local UI state that belongs to one composable.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Short-lived state that does not need process survival.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            SpanText(
                text = "Why does mutableStateOf without remember work sometimes?",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "If you use mutableStateOf in a location where the state object is not recreated during recompositions, it can work without remember",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Without remember: State is re-initialized on every recomposition, leading to lost or inconsistent state.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "With remember but no recomposition: If the composable is not recomposed, mutableStateOf may seem to work without remember, giving the illusion of correctness.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            SpanText(
                text = "Why Use remember and mutableStateOf?",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Using remember with mutableStateOf is the idiomatic way to manage state in Jetpack Compose. It ensures that your UI remains in sync with the underlying data, without the need for manual updates or additional code. The combination of these two functions makes state management intuitive and reduces the likelihood of bugs caused by outdated UI states.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            KotlinCode(
                code = """
                    //1
                    val person by remember {
                      mutableStateOf<Person?>(null, structuralEqualityPolicy())
                    }

                    //2
                    val person by remember {
                      mutableStateOf<Person?>(null, referentialEqualityPolicy())
                    }

                    val firstPerson = Person("Bob")
                    val stillFirstPerson = firstPerson
                    val secondPerson = Person("Jake")

                    // same references, i will not recompose!
                    firstPerson == stillFirstPerson

                    // hey, different references, recompose!
                    firstPerson == secondPerson
                    """.trimIndent()
            )

            SpanText(
                text = "Flow in ViewModel",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "When dealing with heavy computations or long-running tasks, leveraging Flow’s asynchronous capabilities allows for efficient, non-blocking data processing and updating.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Consider a ViewModel that performs a heavy computation and updates a Flow with the result. This computation will be executed on a background thread, exposing the result to the UI layer through a Flow.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            KotlinCode(
                code = """
                    class ComputationViewModel : ViewModel() {
                        private val _result = MutableStateFlow<Int>(0) // Initial value
                        val result: StateFlow<Int> = _result.asStateFlow()

                        fun performComputation(input: Int) {
                            viewModelScope.launch(Dispatchers.Default) {  // Using the Default dispatcher for CPU-intensive task
                                val computedValue = heavyComputation(input)
                                _result.emit(computedValue)  // Emitting from any thread is safe
                            }
                        }

                        private fun heavyComputation(input: Int): Int {
                            // Simulate a heavy computation
                            return input * input
                        }
                    }
                    """.trimIndent()
            )

            SpanText(
                text = "Sharing State: Hoisting",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Hoisting involves lifting the state to a higher-level composable. This promotes reusability and testability.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            KotlinCode(
                code = """
                    @Composable
                    fun EnhancedCounter(count: MutableState<Int>) {
                        Column {
                            Text(count.value)
                            Button(onClick = { count.value++ }) {
                                Text("Increment")
                            }
                        }
                    }
                    """.trimIndent()
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "With the count state being passed in, you can reuse EnhancedCounter in different contexts.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            SpanText(
                text = "Stateful",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            KotlinCode(
                code = """
                    class MyViewModel : ViewModel() {
                        // This can be state flow
                        private val _data = MutableStateFlow<String>()
                        val data: StateFlow<String> get() = _data

                        init {
                            // update the "_data" upon initialization
                        }
                    }

                    @Composable
                    fun MyComposable() {
                        val viewModel: MyViewModel = viewModel()
                        val data by viewModel.data.observeAsState(initial = "Loading...")

                        CustomComposeFunction(data)
                    }
                    """.trimIndent()
            )


            SpanText(
                text = "State management work",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "State management in Jetpack Compose revolves around the concept of a mutable state. State management in Compose uses concepts like remember, mutableStateOf, and state hoisting to manage and share state between Composables. Compose provides the mutableStateOf function to create observable state objects.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "When the state changes, Jetpack Compose automatically recomposes only the affected parts of the UI, ensuring efficient UI updates.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Compose to be aware of state changes, your state values need to be wrapped in a State object. You can do that using the mutableStateOf() function. This will return a MutableState<T> object and Compose will keep track of changes and update the UI whenever you modify the value.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Return a new MutableState initialized with the passed in value.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "The MutableState class is a single value holder whose reads and writes are observed by Compose. Additionally, writes to it are transacted as part of the Snapshot system.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Always use MutableStateFlow in a background thread to avoid blocking the main thread and causing UI freezes. You can use the viewModelScope or CoroutineScope to launch a flow in a background thread.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            SpanText(
                text = "What is MutableState and how does recomposition happen?",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "MutableState is an interface in Compose that holds a value which can be updated. When its value changes, any composable reading it is scheduled for recomposition. Internally, Compose tracks these state reads so that only the dependent parts of the UI are refreshed, making updates efficient and granular.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            KotlinCode(
                code = """
                    val nameState = mutableStateOf("Kotlin Multiplatform")
                    Text(text = nameState.value)
                    //When nameState.value is updated, only this Text will recompose.
                    """.trimIndent()
            )

            SpanText(
                text = "Flow API",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Flow API for handling asynchronous state. This is useful when dealing with data streams that may change over time.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            SpanText(
                text = "collectAsState()",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "collectAsState() is a composable function that collects values from a flow Flow or StateFlow and represents the latest value as a Compose State that updates automatically whenever the state changes (It allows you to convert the flow’s emitted values into Compose’s State type, which makes the values reactive and recomposable). It enables the UI to reactively update when the data in the Flow changes, keeping the interface in sync with the underlying data.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Every time a new flow emission occurs, the State object updates, causing a recomposition of every State.value usage in the Composition.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "The Compose API that converts the StateFlow into a Compose State<T>, automatically triggering recomposition when a new value is emitted.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Ul {
                    Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "collectAsState() is the original API used to collect a Flow inside a composable. It converts the values emitted by a Flow into Compose State. Whenever the flow emits a new value, the composable recomposes and updates the UI.",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }
                }

                KotlinCode(
                    code = """
                        //collectAsState() will keep collecting the flow as long as the composable exists in memory. It does not automatically stop when the screen goes into the background or when the user navigates away.
                        
                        @Composable
                        fun MyComposable(viewModel: MyViewModel) {
                            val state = viewModel.name.collectAsState()

                            Text(text = state.value)
                        }
                    """.trimIndent()
                )

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "name is being collected and converted into Compose’s State object. Whenever the Flow emits a new value, the composable will recompose, ensuring that the UI stays updated.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Ul {
                    SpanText(
                        text = "characteristics of collectAsState()",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )

                    Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "It follows the Composition’s lifecycle, starting to collect the flow when the composable function enters the Composition and stopping when it leaves the Composition.",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }

                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "It keeps the flow collection active even when the Android app is in the background, which can lead to unnecessary resource consumption.",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }

                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "The Flow needs to be collected continuously regardless of UI state.",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }

                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "The composable is short-lived or unlikely to be paused/stopped.",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }

                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "You don’t care about lifecycle-awareness, and resource consumption is not a concern (e.g., simple static screens).",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }
                }
            }

            KotlinCode(
                code = $$"""
                    // 1. Define the UI State as an immutable data class
                    data class UserProfileState(
                        val userName: String = "Guest",
                        val statusMessage: String = "Loading...",
                        val isLoading: Boolean = false,
                        val isError: Boolean = false
                    )

                    // 2. ViewModel: The Single Source of Truth
                    class ProfileViewModel : ViewModel() {
                        // MutableStateFlow is where the state is stored and updated internally
                        private val _state = MutableStateFlow(UserProfileState(isLoading = true))
                        // StateFlow is the public, read-only stream for the Composable
                        val state: StateFlow<UserProfileState> = _state.asStateFlow()

                        init {
                            fetchUserProfile()
                        }

                        private fun fetchUserProfile() {
                            viewModelScope.launch {
                                _state.value = _state.value.copy(isLoading = true, isError = false)
                                // Simulate a network call
                                delay(1500) 
                                
                                // On success
                                _state.value = UserProfileState(
                                    userName = "Astronaut Alex",
                                    statusMessage = "Ready for Launch!",
                                    isLoading = false
                                )
                                
                                // On failure (uncomment to test error state)
                                // _state.value = _state.value.copy(isLoading = false, isError = true, statusMessage = "Network failed.")
                            }
                        }

                        fun onRefreshProfile() {
                            fetchUserProfile()
                        }
                    }

                    // 3. Composable: Observes the state and renders the UI
                    @Composable
                    fun UserProfileScreen(viewModel: ProfileViewModel = viewModel()) {
                        // 'state' will automatically recompose whenever the ViewModel emits a new value
                        val userState by viewModel.state.collectAsState()

                        Column(
                            Modifier
                                .fillMaxSize()
                                .padding(20.dp),
                            horizontalAlignment = Alignment.CenterHorizontally
                        ) {
                            if (userState.isLoading) {
                                CircularProgressIndicator()
                            } else if (userState.isError) {
                                Text("Error loading profile. Tap to retry.")
                            } else {
                                Text("Hello, ${userState.userName}!", style = MaterialTheme.typography.headlineMedium)
                                Text(userState.statusMessage, style = MaterialTheme.typography.bodyLarge)
                            }

                            Spacer(Modifier.height(16.dp))

                            Button(onClick = viewModel::onRefreshProfile, enabled = !userState.isLoading) {
                                Text("Refresh Profile")
                            }
                        }
                    }
                    """.trimIndent()
            )

            SpanText(
                text = "Handling Initial State",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "collectAsState() requires an initial value to display before any data is emitted by the Flow. This ensures that the UI has a valid state to render immediately.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            KotlinCode(
                code = """
                    val state by viewModel.uiState.collectAsState(initial = "Initial Value")
                    """.trimIndent()
            )

            SpanText(
                text = "Use collectAsState() when",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "The Flow needs to be collected continuously regardless of UI state.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "The composable is short-lived or unlikely to be paused/stopped.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "You don’t care about lifecycle-awareness, and resource consumption is not a concern (e.g., simple static screens).",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            SpanText(
                text = "What You Need to Know About collectAsState()",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "No Built-In Lifecycle Awareness: The key thing to remember about collectAsState() is that it isn’t lifecycle-aware by default. It will collect the flow as long as the composable is active, even when it might not be visible on the screen. This can lead to unnecessary resource consumption if the composable is inactive, but still collecting values.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Resource-Intensive in Inactive States: Since collectAsState() doesn’t stop collecting when the composable is not on screen or in a paused state, it can potentially keep observing data and trigger recompositions, even when the UI is not visible. In many cases, this can result in inefficient use of resources, such as power and CPU.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Use Case: collectAsState() works well when the composable is short-lived or always visible. But when dealing with composables that can go in and out of view (e.g., in a LazyColumn or when navigating between screens), collectAsState() may not be the most efficient choice.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            SpanText(
                text = "The Problem with collectAsState()",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "collectAsState doesn't know when your composable is visible. It starts collecting as soon as the composable enters the composition —and keeps doing so even if the screen is not in the foreground.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "That means",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )

                    Ul {
                        Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                            SpanText(
                                text = "You could be running unnecessary background work.",
                                modifier = Modifier
                                    .fontSize(FontSize.Medium)
                            )
                        }

                        Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                            SpanText(
                                text = "Network calls or database queries might happen when the user isn't looking.",
                                modifier = Modifier
                                    .fontSize(FontSize.Medium)
                            )
                        }

                        Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                            SpanText(
                                text = "Your app wastes CPU cycles and battery.",
                                modifier = Modifier
                                    .fontSize(FontSize.Medium)
                            )
                        }
                    }
                }

                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "In other words, it's not lifecycle-aware.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            SpanText(
                text = "viewState",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "viewState is a State object that holds the current state of the viewModel and updates the UI accordingly. It is collected using the collectAsStateWithLifecycle extension function, which ensures that the state is collected in a lifecycle-aware manner.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            SpanText(
                text = "collectAsStateWithLifecycle",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "collectAsStateWithLifecycle() (from androidx.lifecycle:lifecycle-runtime-compose) to automatically handle cancellation when the composable goes out of scope.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Observe ViewModel Ul state safely. Use when your ViewModel exposes StateFlow.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "collectAsStateWithLifecycle() is a composable function that collects values from a Flow and represents the latest value as a Compose State in a lifecycle-aware manner. Every time a new Flow emission occurs, the State object updates, triggering a recomposition wherever State.value is used in the Composition.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                //https://proandroiddev.com/the-evolution-of-flow-collection-in-jetpack-compose-e93416a9d52d
                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "The API collects the Flow when the lifecycle is active and exposes its latest value as Compose State. For a StateFlow, the current value provides the initial state. You can also specify a minimum active lifecycle state or an explicit initial value when needed.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "It collects data only when the screen is visible and active.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "It only collects the Flow when the composable’s lifecycle is at least in the STARTED state. When the user navigates away from this screen, the Flow collection automatically stops. When the user comes back, it resumes again. You do not need to manually handle lifecycle changes.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "This ensures your flow collection is automatically tied to the composable’s lifecycle no leaks, no background collectors, no jank.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            KotlinCode(
                code = """
                    val uiState by viewModel.uiState.collectAsStateWithLifecycle()
                    """.trimIndent()
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Starts collecting when screen is visible",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Stops when app goes to background",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Resumes automatically",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Prevents wasted work & battery drain",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            KotlinCode(
                code = """
                    //Emitting Large Data Too Frequently
                    val productsFlow = flow {
                        while (true) {
                            emit(api.getProducts()) // emits a new large list every 100ms
                            delay(100)
                        }
                    }
                    
                    val productsFlow = api.observeProducts()
                        .distinctUntilChanged() //Emit only if data truly changes
                        .debounce(300)          //Wait for stability before emitting
                        .flowOn(Dispatchers.IO) //Keep heavy work off main thread
                        
                    val heavyFlow = flow {
                        emit(fetchData()) // runs on IO thread
                    }.flowOn(Dispatchers.IO)   
                    
                    //Recomposition Storms in Compose
                    val positionFlow = snapshotFlow { lazyListState.firstVisibleItemScrollOffset }
                        .sample(200) // Limit recompositions
                        .distinctUntilChanged()

                    val position by positionFlow.collectAsStateWithLifecycle()
                    """.trimIndent()
            )

            SpanText(
                text = "Collection Is Not the Same as Refreshing",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "When collectAsStateWithLifecycle() stops collecting, it does not necessarily stop the entire upstream Flow. The actual behavior depends on how that Flow is constructed.",
                        modifier = Modifier.fontSize(FontSize.Medium)
                    )
                }
            }

            KotlinCode(
                code = """
                    val uiState = repository.observeItems()
                        .map { items ->
                        UiState(items = items)
                    }
                    .stateIn(
                        scope = viewModelScope,
                        started = SharingStarted.WhileSubscribed(5_000),
                        initialValue = UiState()
                    )
                    """.trimIndent()
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Here, WhileSubscribed(5_000) keeps the upstream Flow active for five seconds after the last subscriber disappears. If collection resumes within that window, the upstream does not need to restart.",
                        modifier = Modifier.fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "If no subscribers remain beyond that timeout, the upstream collection stops. When a subscriber returns, the upstream collection starts again.",
                        modifier = Modifier.fontSize(FontSize.Medium)
                    )
                }
            }

            SpanText(
                text = "collectAsStateWithLifecycle() for Lifecycle-Aware Collection",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "This is an enhanced version of collectAsState() that integrates directly with the lifecycle of a composable. Sometimes there will be unnecessary flow collection will happen even if screen is closed. To fix this problem google released this new method to collect flows safely inside composables",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = " In scenarios where the Flow may emit values while the composable is not in a visible lifecycle state (such as paused or stopped), it’s recommended to use collectAsStateWithLifecycle, which is lifecycle-aware and only collects when the composable is in an active lifecycle state.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "You want automatic handling of lifecycle states for resource efficiency.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "The composable is part of a dynamic or multi-screen app, where the user navigates between different screens.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "You want to ensure that data collection only happens when the composable is active, and the UI is visible. (It stops collecting when the UI is not visible, such as when the user navigates away)",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            SpanText(
                text = "Key Features of collectAsStateWithLifecycle()",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Lifecycle Awareness: The most significant difference is that collectAsStateWithLifecycle() is lifecycle-aware. This means that it will start collecting the Flow only when the composable is in a STARTED state or higher (i.e., when the composable is visible and active). It automatically stops collecting when the composable is paused or stopped. This makes it more efficient than collectAsState() when it comes to handling resources during state transitions.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Automatic Pausing and Resuming: When the composable goes into the background (e.g., the user navigates away from the screen or the app is minimized), collectAsStateWithLifecycle() stops collecting the Flow, preventing unnecessary recompositions and resource use. Once the composable is active again, it resumes collection.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Less Resource Overhead: By pausing collection during inactive states, this function reduces the strain on system resources, like CPU and battery, especially in complex UIs with multiple state observers.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Consistency with ViewModel Lifecycles: Since many apps use ViewModels, which themselves are lifecycle-aware, combining collectAsStateWithLifecycle() with ViewModels creates a cohesive flow of state management that aligns perfectly with Android’s lifecycle components.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Use Case: collectAsStateWithLifecycle() is the better choice when your composable needs to observe a Flow that should pause and resume based on the lifecycle state of the composable or the app. It is particularly useful in screens that the user navigates away from or in UIs that are dynamically created and destroyed, like lists or paginated content.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            SpanText(
                text = "Use collectAsStateWithLifecycle() when",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "You want automatic handling of lifecycle states for resource efficiency.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "The composable is part of a dynamic or multi-screen app, where the user navigates between different screens.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "You want to ensure that data collection only happens when the composable is active, and the UI is visible.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "You’re optimizing for performance, especially in battery- or resource-sensitive applications.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            SpanText(
                text = "collectAsStateWithLifecycle() Work",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "It automatically starts and stops collecting values based on the UI lifecycle.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )

                    Ul {
                        Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                            SpanText(
                                text = "Starts collecting when screen is visible",
                                modifier = Modifier
                                    .fontSize(FontSize.Medium)
                            )
                        }

                        Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                            SpanText(
                                text = "Stops when app goes to background.",
                                modifier = Modifier
                                    .fontSize(FontSize.Medium)
                            )
                        }

                        Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                            SpanText(
                                text = "Resumes automatically",
                                modifier = Modifier
                                    .fontSize(FontSize.Medium)
                            )
                        }
                    }
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "By default, it follows Lifecycle.State.STARTED, meaning collection begins when the UI is visible and stops when it's no longer active.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "You can configure the lifecycle state using the minActiveState parameter.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            WebImage(src = Resources.Images.COLLECT_AS_STATE_WITH_LIFECYCLE,
                modifier = Modifier.fillMaxWidth().padding(5.px).borderRadius(10.px),
                contentDescription = "collectAsStateWithLifecycle")

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Opens app → STARTED → coroutine starts",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Presses Home → STOPPED → coroutine cancels ",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Returns → STARTED → coroutine restarts",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Presses Back → DESTROYED → everything cleaned",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            SpanText(
                text = "Lifecycle Awareness - (collectAsStateWithLifecycle(), DisposableEffect(), Observing Raw Lifecycle States via LocalLifecycleOwner)",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Composables have their own lifecycle, which leads finally to the requirement of converting our streams to State objects, we still need to take the actual app's lifecycle when it comes to observing or, in the case of Flow objects, collecting streams.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Ul {
                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "Jetpack Compose we often don't directly use the items of a stream but convert them into a State.",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }

                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "Because we still listen to streams that potentially can be triggered while the app is not present or remain over the app lifecycle without getting properly handled, we need to introduce a new toolkit of functions on how to handle these cases.",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }

                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "Use the collectAsStateWithLifecycle API to collect flows in a lifecycle-aware manner from your UI.",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }
                }
            }

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Lifecycle-aware components in Jetpack Compose, such as LaunchedEffect, DisposableEffect, and collectAsStateWithLifecycle() automatically manage resources based on composable lifecycle events (Update(), onEnter, onExit, onRecompose). They prevent memory leaks and optimize performance by pausing, resuming, or cleaning up data subscriptions based on UI visibility (e.g., STARTED state).",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Automatically updates the UI only when the lifecycle is in an appropriate state (e.g., when the activity or fragment is active).",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            SpanText(
                text = "c𝒐𝒍𝒍𝒆𝒄𝒕𝑨𝒔𝑺𝒕𝒂𝒕𝒆𝑾𝒊𝒕𝒉𝑳𝒊𝒇𝒆𝒄𝒚𝒄𝒍𝒆()",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Similar to collectAsState(), but it’s lifecycle-aware. This means it only collects the Flow when the composable is in an active state (STARTED or RESUMED), preventing unnecessary work and potential memory leaks.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "collectAsStateWithLifecycle allows your app to save app resources when not needed, such as when the app is in the background. Keeping resources alive unnecessarily can impact the user's device health. Such resources may include firebase queries, location or network updates, and database connections.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Use 𝒄𝒐𝒍𝒍𝒆𝒄𝒕𝑨𝒔𝑺𝒕𝒂𝒕𝒆𝑾𝒊𝒕𝒉𝑳𝒊𝒇𝒆𝒄𝒚𝒄𝒍𝒆() for better lifecycle management, ensuring your Flow is only collected when the composable is active, leading to more efficient resource usage",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Uses `𝒄𝒐𝒍𝒍𝒆𝒄𝒕𝑨𝒔𝑺𝒕𝒂𝒕𝒆𝑾𝒊𝒕𝒉𝑳𝒊𝒇𝒆𝒄𝒚𝒄𝒍𝒆()` to achieve the same result, but with lifecycle awareness, collecting data only when the composable is active.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "When your app goes in the background(Home button is pressed) It stops collecting the flow allowing you to release app resources when not needed for better user experience and device's health.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

            }

            SpanText(
                text = "𝗰𝗼𝗹𝗹𝗲𝗰𝘁𝗔𝘀𝗦𝘁𝗮𝘁𝗲()",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Converts a Flow into a Compose State, re-composing the UI when new data is emitted. However, it doesn't account for the lifecycle state of the composable.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Use 𝗰𝗼𝗹𝗹𝗲𝗰𝘁𝗔𝘀𝗦𝘁𝗮𝘁𝗲() when you are sure that the composable’s lifecycle is managed, and you do not need additional lifecycle awareness",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Uses `𝒄𝒐𝒍𝒍𝒆𝒄𝒕𝑨𝒔𝑺𝒕𝒂𝒕𝒆()` to convert `𝒔𝒂𝒎𝒑𝒍𝒆𝑭𝒍𝒐𝒘` from the `SampleViewModel` into a Compose state, updating the UI with each new value.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "When app goes into background, It continues to collect flow unnecessarily exploiting app resources (eg:- by keeping network or location updates active).",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            SpanText(
                text = "Lifecycle-Aware Components Compose (DefaultLifecycleObserver, @OnLifecycleEvent (Deprecated), collectAsStateWithLifecycle())",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Compose’s lifecycle awareness ensures that when your Activity or Fragment moves through various lifecycle states, your composables’ re-compositions are managed accordingly. However, if you’re using side effects such as LaunchedEffect, DisposableEffect, or any flow collection, you need to ensure they are scoped correctly.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "For example, LaunchedEffect runs a coroutine the first time your composable is placed in composition, and can be canceled if the composable leaves the composition. This means you can perform tasks like data loading from the ViewModel without memory leaks, as the system will handle cancellation automatically when the composable is no longer visible.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            KotlinCode(
                code = """
                    class AnalyticsObserver : DefaultLifecycleObserver {

                    override fun onResume(owner: LifecycleOwner) {
                            println("App in foreground")
                        }


                    override fun onPause(owner: LifecycleOwner) {
                            println("App in background")
                        }
                    }
                    
                    private val analyticsObserver = AnalyticsObserver()
                    lifecycle.addObserver(analyticsObserver)
                    """.trimIndent()
            )

            SpanText(
                text = "Lifecycle-Aware Collection - repeatOnLifecycle, flowWithLifecycle, StateFlow",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            SpanText(
                text = "repeatOnLifecycle",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "It automatically stops and restarts your Flow collection based on lifecycle state. - wrapping your collection block and targeting Lifecycle.State. STARTED. Collection runs only when the UI is alive. It automatically cancels the collection coroutine when the user puts the app in the background. It resumes the moment the app comes back to the foreground.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            KotlinCode(
                code = """
                    lifecycleScope.launch {
                        repeatOnLifecycle(Lifecycle.State.STARTED) {
                            myFlow.collect { value ->
                                // UI updates, no drama
                            }
                        }
                    }
                    """.trimIndent()
            )

            SpanText(
                text = "flowWithLifecycle",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            KotlinCode(
                code = """
                    myFlow.flowWithLifecycle(lifecycle, Lifecycle.State.STARTED)
                    .collectLatest { value ->
                        // Smooth UI updates
                    }
                    """.trimIndent()
            )

            //https://github.com/XDdevv/PLJuneMiniChallenges.git
            KotlinCode(
                code = """
                    //Event Handle 
                    
                    @Composable
                    fun <T> ObserveAsEvents(
                        flow: Flow<T>,
                        key1: Any? = null,
                        key2: Any? = null,
                        onEvent: (T) -> Unit
                    ) {
                        val lifecycleOwner = LocalLifecycleOwner.current
                        LaunchedEffect(lifecycleOwner.lifecycle, key1, key2) {
                            lifecycleOwner.repeatOnLifecycle(Lifecycle.State.STARTED) {
                                withContext(Dispatchers.Main.immediate) {
                                    flow.collect(onEvent)
                                }
                            }
                        }
                    }
                    """.trimIndent()
            )

            SpanText(
                text = "Observing Raw Lifecycle States via LocalLifecycleOwner",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "If your UI logic strictly depends on whether the app is currently RESUMED or STARTED, you can observe the lifecycle state directly within your Composable function using LocalLifecycleOwner.",
                        modifier = Modifier.fontSize(FontSize.Medium)
                    )
                }
            }

            KotlinCode(
                code = """
                    @Composable
                    fun CameraPreviewOverlay() {
                        val lifecycleOwner = LocalLifecycleOwner.current
                        val lifecycleState by lifecycleOwner.lifecycle.currentStateAsState()

                        if (lifecycleState.isAtLeast(Lifecycle.State.RESUMED)) {
                            // Only run heavy camera preview logic when the screen is fully active
                            ActiveOverlay()
                        } else {
                            PausedOverlay()
                        }
                    }
                    """.trimIndent()
            )

            SpanText(
                text = "StateFlow",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "collect when you can retain the last emitted value and never miss an update?",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Lives in ViewModel or business layer. Can be collected inside Compose with collectAsState(). Designed for cross-layer shared state.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            KotlinCode(
                code = """
                    class MyViewModel : ViewModel() {
                        private val _uiState = MutableStateFlow("Initial Value")
                        val uiState: StateFlow<String> = _uiState
                    }
                    """.trimIndent()
            )

            SpanText(
                text = "SnapshotStateList",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "SnapshotStateList and SnapshotStateMap are special types of collections in Jetpack Compose designed to work efficiently with Compose's state system. In the context of SnapshotStateList, snapshot refers to a mechanism for capturing a static, immutable version of the list's state at a specific point in time.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Ul {
                    Li(Modifier.padding(top = 1.em).toAttrs()) {
                        SpanText(
                            text = "Observability",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                                .fontWeight(FontWeight.Bold)
                        )
                    }

                    Ul {
                        Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                            SpanText(
                                text = "SnapshotStateList is a special type of mutable list that integrates with Jetpack Compose's state observation system. When the list's contents change, Compose recomposes any composable functions that depend on it, updating the UI accordingly. (These collections are observable, meaning that when their content changes, they trigger recomposition in any composables that depend on them. They are useful for managing lists and maps in a way that Compose can track changes and update the UI accordingly)",
                                modifier = Modifier
                                    .fontSize(FontSize.Medium)
                            )
                        }
                    }
                }

                Ul {
                    Li(Modifier.padding(top = 1.em).toAttrs()) {
                        SpanText(
                            text = "Snapshotting",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                                .fontWeight(FontWeight.Bold)
                        )
                    }

                    Ul {
                        Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                            SpanText(
                                text = "When a change is made to a SnapshotStateList, a new snapshot is created instead of modifying the original list directly. This snapshot is a separate, immutable collection that represents the list's state at a particular moment.",
                                modifier = Modifier
                                    .fontSize(FontSize.Medium)
                            )
                        }
                    }
                }

                Li(Modifier.padding(top = 1.em).toAttrs()) {
                    SpanText(
                        text = "Snapshotting Works",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Ul {
                    Li(Modifier.padding(top = 1.em).toAttrs()) {
                        SpanText(
                            text = "Initial State",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                                .fontWeight(FontWeight.Bold)
                        )
                    }

                    Ul {
                        Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                            SpanText(
                                text = "When you create a SnapshotStateList using mutableStateListOf, a snapshot is created to represent the initial empty list.",
                                modifier = Modifier
                                    .fontSize(FontSize.Medium)
                            )
                        }
                    }
                }

                Ul {
                    Li(Modifier.padding(top = 1.em).toAttrs()) {
                        SpanText(
                            text = "Modifications",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                                .fontWeight(FontWeight.Bold)
                        )
                    }

                    Ul {
                        Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                            SpanText(
                                text = "When you add, remove, or modify elements in the list, a new snapshot is generated that reflects the updated state. The previous snapshot remains intact, preserving the previous state.",
                                modifier = Modifier
                                    .fontSize(FontSize.Medium)
                            )
                        }
                    }
                }

                Ul {
                    Li(Modifier.padding(top = 1.em).toAttrs()) {
                        SpanText(
                            text = "Composition",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                                .fontWeight(FontWeight.Bold)
                        )
                    }

                    Ul {
                        Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                            SpanText(
                                text = "Composable functions that use the SnapshotStateList internally hold a reference to a snapshot. When a new snapshot is created, Compose compares it to the currently held snapshot and recomposes only if changes are detected.",
                                modifier = Modifier
                                    .fontSize(FontSize.Medium)
                            )
                        }
                    }
                }
            }

            KotlinCode(
                code = """
                    //SnapshotStateList Dynamic LazyColumn
                    
                    private val _uiState = MutableStateFlow(CreateTaskScreenUIState())
                    val uiState = _uiState.asStateFlow()

                    fun onDone(value: String) {
                        _uiState.update {
                            it.copy(
                                checkList = uiState.value.checkList.apply { add(value) },
                                checkItem = ""
                            )
                        }
                    }

                    data class CreateTaskScreenUIState(
                        val checkList: SnapshotStateList<String> = mutableStateListOf()
                    )
                    """.trimIndent()
            )

            KotlinCode(
                code = """
                    @Composable
                    fun SettingsScreen() {
                        val settings = remember {
                            mutableStateMapOf(
                                "Notifications" to true,
                                "Dark Mode" to false,
                                "Location Access" to true
                            )
                        }

                        Column {
                            settings.forEach { (key, value) ->
                                Row {
                                    Text(text = key)
                                    Switch(checked = value, onCheckedChange = { newValue ->
                                        settings[key] = newValue
                                    })
                                }
                            }
                        }
                    }
                    """.trimIndent()
            )

            SpanText(
                text = "Differences between SnapshotStateList and ArrayList",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "SnapshotStateList is reactive; changes automatically trigger recomposition in Compose.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "ArrayList is not reactive and requires manual notifications for UI updates.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            SpanText(
                text = "MutableStateFlow onSubscription",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "onSubscription is a powerful feature of MutableStateFlow that allows you to perform actions when a collector starts observing the flow. This can be incredibly useful in scenarios where you need to trigger certain operations only when there’s an active subscriber.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Lazy Initialization: Start certain operations only when they are needed.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Resource Management: Allocate resources when there’s an active subscriber and release them when there are none.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Optimized Data Flow: Manage data emissions based on active subscriptions.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            KotlinCode(
                code = """
                    //1
                    private val _stateFlow = MutableStateFlow(value: 0)

                    val monitoredFlow = _stateFlow.onSubscription {
                        println("A new subscriber is active! *")
                        // Perform any actions needed on new subscription
                    }

                    //2
                    // MutableStateFlow holding the current state of the subscription
                    private val _subscriptionState = MutableStateFlow(SubscriptionState())

                    // Expose the state as an immutable StateFlow
                    val subscriptionState = _subscriptionState
                        .onSubscription {
                            // Perform an action when a new subscription starts
                            fetchInitialSubscriptionStatus()
                        }.asStateFlow()
                    """.trimIndent()
            )

            SpanText(
                text = "Log Print",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            KotlinCode(
                code = """
                     private val TAG = this::class.java.simpleName
                    """.trimIndent()
            )

            SpanText(
                text = "Lazy initialize",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            KotlinCode(
                code = """
                    //by lazy { ... } → initializes blogList only when first accessed.
                    //mutableListOf<Blog>() → creates an empty mutable list of Blog objects.
                    
                    val blogList by lazy { mutableListOf<Blog>() }
                    
                    public val encodedPrivateKey: String by lazy { }
                    """.trimIndent()
            )

            KotlinCode(
                code = """
                    val items = remember { mutableListOf("Car", "Bike") }
                    items.add("Train") // UI Will Not Update Here
                    
                    val names by remember { mutableStateListOf("Android", "Kotlin") }
                    names.add("Jetpack") // UI will update automatically
                    """.trimIndent()
            )

            SpanText(
                text = "MutableStateFlow CRUD Operations",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            KotlinCode(
                code = """
                    class PreviewTaskViewModel : ViewModel() {
                        private  val _tasks = mutableStateListOf(
                            Task(1, "Task 1", "Description 1"),
                            Task(2, "Task 2", "Description 2"),
                            Task(3, "Task 3", "Description 3"))

                        val tasks: List<Task> get() = _tasks

                        fun toggleTaskCompletion(task: Task) {
                            val index = _tasks.indexOf(task)
                            if (index != -1) {
                                _tasks[index] = task.copy(isCompleted = !task.isCompleted)
                            }
                        }

                        fun deleteTask(task: Task) {
                            _tasks.remove(task)
                        }
                    }
                """.trimIndent()
            )

            KotlinCode(
                code = """
                    //getArticles() is called once, during ViewModel initialization.
                    //That means _articles is initialized once with the fake list.
                    //Because you never update _articles.value anywhere later, it will not change after initialization.
                    
                    import androidx.lifecycle.ViewModel
                    import kotlinx.coroutines.flow.MutableStateFlow
                    import kotlinx.coroutines.flow.asStateFlow

                    data class Article(
                        val author: String,
                        val title: String
                    )

                    class ArticleViewModel : ViewModel() {
                        // Fake article list
                        private val fakeArticles = listOf(
                            Article(
                                author = "Alice Johnson",
                                title = "The Rise of Kotlin Multiplatform"
                            ),
                            Article(
                                author = "Bob Smith",
                                title = "Exploring Clean Architecture in Android"
                            ),
                            Article(
                                author = "Charlotte Brown",
                                title = "How to Effectively Test Asynchronous Code"
                            )
                        )

                        // Backing MutableStateFlow
                        private val _articles = MutableStateFlow(getArticles())
                        val articles = _articles.asStateFlow()

                        // Function returning the fake list
                        private fun getArticles(): List<Article> = fakeArticles
                    }
                    
                    //If you want to update write code
                    class ArticleViewModel : ViewModel() {

                        private val fakeArticles = listOf(
                            Article("Alice Johnson", "The Rise of Kotlin Multiplatform"),
                            Article("Bob Smith", "Exploring Clean Architecture in Android"),
                            Article("Charlotte Brown", "How to Effectively Test Asynchronous Code")
                        )

                        private val _articles = MutableStateFlow(fakeArticles)
                        val articles = _articles.asStateFlow()

                        fun addArticle(article: Article) {
                            _articles.value = _articles.value + article
                        }

                        fun removeArticle(article: Article) {
                            _articles.value = _articles.value - article
                        }
                    }
                    """.trimIndent()
            )

            KotlinCode(
                code = """
                    //Ticket Booking (Add To Cart Logic)
                    
                    private val _uiState = MutableStateFlow(TicketUiState())
                    val uiState = _uiState.asStateFlow()

                    fun increaseNumberAdultsSeats() {
                        val updatedNumber = uiState.value.numberAdultsSeats.plus(1)
                        _uiState.update { currentState -> currentState.copy(numberAdultsSeats = updatedNumber) }
                    }

                    fun decreaseNumberAdultsSeats() {
                        val updatedNumber = uiState.value.numberAdultsSeats.minus(1)
                            if (updatedNumber >= 1) {
                                _uiState.update { currentState -> currentState.copy(numberAdultsSeats = updatedNumber) }
                            }
                    }

                    import java.time.LocalDate
                    data class TicketUiState (
                        val numberAdultsSeats: Int = 1,
                        val numberChildrenSeats: Int = 0,
                        var departureDate: LocalDate = LocalDate.now()
                    )
                    """.trimIndent()
            )

            //Improve Code StateFlow
            KotlinCode(
                code = """
                    //Common misuse is creating a StateFlow for each call. That makes no sense. We create StateFlow so it can be shared by multiple flows. StateFlow should be stored in a property; only then can it be shared by multiple flows.

                    //Wrong Way
                    fun updatesFlow() = useCase.updatesFlow().
                        .shareIn(
                            scope = viewModelScope,
                            started = SharingStarted.WhileSubscribed(),
                        )

                    //Good Way
                     private val updatesFlow() = useCase.updatesFlow().
                        .shareIn(
                            scope = viewModelScope,
                            started = SharingStarted.WhileSubscribed(),
                        )

                     fun updatesFlow() = updatesFlow
                    """.trimIndent()
            )

            KotlinCode(
                code = """
                    context (viewModel: ViewModel)
                    fun <T> Flow<T>.stateInWhileSubscribed(initialValue: T): StateFlow<T> {
                        return this.stateIn(
                            viewModel.viewModelScope,
                            started = SharingStarted.WhileSubscribed(stopTimeoutMillis = 5000),
                            initialValue
                        )
                    }
                    """.trimIndent()
            )

            //https://github.com/Narsuf/Stonks.git
            KotlinCode(
                code = """
                    inline fun <T, reified Type : T> MutableStateFlow<T>.updateIfType(function: (Type) -> T) {
                        update { it.runIfType<_, Type> { function(this) } }
                    }
                    
                    internal sealed class HomeState {

                        data object Idle : HomeState()
                        data object Loading: HomeState()
                        data object Error: HomeState()

                        data class Content(
                            val bottomSheet: BottomSheet,
                            val isWatchlistLoading: Boolean,
                            val watchlist: ImmutableList<Item>,
                            val isEndReached: Boolean,
                            val isPageLoading: Boolean,
                        ) : HomeState() {

                            data class BottomSheet(
                                val epsGrowthInput: BigDecimal,
                                val valuationFloorInput: BigDecimal,
                            )

                            data class Item(
                                val icon: ImageBitmap?,
                                val name: String,
                                val symbol: String,
                                val price: String?,
                                val targetPrice: DeltaTextEntity?,
                                val extraValue: String?,
                            )
                        }
                    }

                    private val state = MutableStateFlow<HomeState>(Idle)
                    internal val viewState = state.asStateFlow()
                    
                     viewModelScope.launch {
                        state.updateIfType { c: Content -> c.copy(isPageLoading = true) }
                     }
                    """.trimIndent()
            )

            //https://github.com/pitampoudel/komposeauth.git
            KotlinCode(
                code = """
                    //Profile Edit State - Event
                    
                    //InfoMessage.kt
                    sealed interface InfoMessage {
                        val text: String

                        data class General(override val text: String) : InfoMessage
                        data class Success(override val text: String) : InfoMessage
                        data class Error(override val text: String) : InfoMessage
                    }
                    
                    fun Throwable?.toInfoMessage(): InfoMessage {
                        return InfoMessage.Error(this?.message.orEmpty())
                    }
                    
                    //RegisterPublicKeyRequest.kt
                    @Serializable
                    data class RegisterPublicKeyRequest(val publicKey: PublicKeyCredential){
                        @Serializable
                        data class PublicKeyCredential(val credential: JsonObject, val label: String)
                    }

                    //ProfileEvent.kt
                    sealed interface ProfileEvent {
                        data class RegisterPublicKey(val credential: RegisterPublicKeyRequest.PublicKeyCredential) : ProfileEvent
                        data class InfoMsgChanged(val msg: InfoMessage?) : ProfileEvent
                        data object LogOut : ProfileEvent
                        data class Deactivate(val confirmed: Boolean) : ProfileEvent
                        data object DismissDeactivateConfirmation : ProfileEvent
                        
                        //Access ProfileEvent.EditEvent.GivenNameChanged()
                        /*
                        fun onEvent(event: ProfileEvent) {
                            viewModelScope.launch {
                                is ProfileEvent.Deactivate -> {
                                    if (event.confirmed) {
                                    }
                                }
                            }
                        }
                        */
                        sealed interface EditEvent : ProfileEvent {
                            data class GivenNameChanged(val value: String) : EditEvent
                            data class FamilyNameChanged(val value: String) : EditEvent
                            object Submit : EditEvent
                        }
                    }
                    
                    //ProfileState.kt
                    data class ProfileState(
                        val progress: Float? = null,
                        val infoMsg: InfoMessage? = null,
                        val profile: ProfileResponse? = null,
                        val askingDeactivateConfirmation: Boolean = false,
                        val editingState: EditingState = EditingState(),
                        val webAuthnRegistrationOptions: String? = null
                    ) {
                        data class EditingState(
                            val givenName: String = "",
                            val givenNameError: GeneralValidationError? = null,
                            val familyName: String = "",
                            val familyNameError: GeneralValidationError? = null
                        ) {
                            fun hasError(): Boolean {
                                return givenNameError != null || familyNameError != null
                            }
                            fun toRequest() = if (!hasError()) UpdateProfileRequest(
                                givenName = givenName,
                                familyName = familyName
                            ) else null

                        }
                    }
                    
                    //OTP State - Event
                    
                    //UpdatePhoneNumberRequest.kt
                    @Serializable
                    data class UpdatePhoneNumberRequest(
                        @SerialName("phoneNumber")
                        val phoneNumber: String,
                        @SerialName("countryCode")
                        val countryCode: String? = null
                    )
                    
                    //OtpEvent.kt
                    sealed interface OtpEvent {
                        data object DismissInfoMsg : OtpEvent
                        class CodeChanged(val value: String) : OtpEvent
                        object SendOtp : OtpEvent
                        class SubmitPhoneNumber(val req: UpdatePhoneNumberRequest) : OtpEvent
                        data object Verify : OtpEvent
                    }
                    
                    //OtpState.kt
                    data class OtpState(
                        val progress: Float? = null,
                        val infoMsg: InfoMessage? = null,
                        val req: UpdatePhoneNumberRequest? = null,
                        val code: String = "",
                        val codeError: GeneralValidationError? = null
                    ) {
                        fun containsError() = codeError != null
                        fun verifyParam() = if (containsError() || req == null) null else VerifyPhoneOtpRequest(
                            phoneNumber = req.phoneNumber,
                            countryCode = req.countryCode,
                            otp = code
                        )
                    }
                    
                    //Credential.kt
                    @Serializable
                    sealed class Credential {
                        @Serializable
                        @SerialName("GOOGLE_ID")
                        class GoogleId(
                            @SerialName("idToken")
                            val idToken: String
                        ) : Credential()

                        @Serializable
                        @SerialName("APPLE_ID")
                        class AppleId(
                            @SerialName("idToken")
                            val idToken: String
                        ) : Credential()

                        @Serializable
                        @SerialName("AUTH_CODE")
                        class AuthCode(
                            @SerialName("platform")
                            val platform: Platform,
                            @SerialName("code")
                            val code: String,
                            @SerialName("codeVerifier")
                            val codeVerifier: String,
                            @SerialName("redirectUri")
                            val redirectUri: String
                        ) : Credential()

                        @Serializable
                        @SerialName("USERNAME_PASSWORD")
                        class UsernamePassword(
                            @SerialName("username")
                            val username: String,
                            @SerialName("password")
                            val password: String
                        ) : Credential()

                        @Serializable
                        @SerialName("PUBLIC_KEY")
                        data class PublicKey(
                            @SerialName("authenticationResponseJson")
                            val authenticationResponseJson: String
                        ) : Credential()

                        @Serializable
                        @SerialName("REFRESH_TOKEN")
                        data class RefreshToken(
                            @SerialName("refreshToken")
                            val refreshToken: String
                        ) : Credential()
                    }
                    """.trimIndent()
            )

            //https://github.com/SkyD666/Compone.git
            KotlinCode(
                code = """
                    @Composable
                    fun suspendString(block: suspend () -> String): String {
                        return suspendString(block) { block() }
                    }

                    @Composable
                    fun <T : Any> suspendString(value: T, block: suspend (T) -> String): String {
                        var str by rememberSaveable { mutableStateOf("") }
                        LaunchedEffect(value, block) {
                            str = block(value)
                        }
                        return str
                    }
                    
                    fun blockString(resource: StringResource): String = runBlocking {
                        getString(resource)
                    }

                    fun blockString(resource: StringResource, vararg formatArgs: Any): String = runBlocking {
                        getString(resource, *formatArgs)
                    }

                    fun blockPluralString(resource: PluralStringResource, quantity: Int): String = runBlocking {
                        getPluralString(resource, quantity)
                    }

                    fun blockPluralString(
                        resource: PluralStringResource,
                        quantity: Int,
                        vararg formatArgs: Any
                    ): String = runBlocking {
                        getPluralString(resource, quantity, *formatArgs)
                    }
                    """.trimIndent()
            )

            KotlinCode(
                code = """
                    import kotlin.time.Clock

                    internal val dateFormatterPatterns =
                      setOf(
                        // Keep the two character year before parsing the four
                        // character year of similar pattern. Not sure why,
                        // but unlike JVM, iOS is not keep it strict?
                        "E, d MMM yy HH:mm:ss Z",
                        "E, d MMM yyyy HH:mm:ss O",
                        "E, d MMM yyyy HH:mm:ss Z",
                        "E, d MMM yyyy HH:mm:ss z",
                        "E, d MMM yyyy HH:mm Z",
                        "E, dd MMM yyyy",
                        "d MMM yyyy HH:mm:ss z",
                        "dd MMM yyyy HH:mm Z",
                        "yyyy-MM-dd'T'HH:mm:ssz",
                        "yyyy-MM-dd'T'HH:mm:ssZ",
                        "yyyy-MM-dd'T'HH:mm:ss",
                        "yyyy-MM-dd HH:mm:ss",
                        "yyyy-MM-dd HH:mm:ss z",
                        "yyyy-MM-dd",
                        "MM-dd HH:mm:ss",
                        "E, d MMM yyyy HH:mm:ss zzzz",
                        "yyyy-MM-dd'T'HH:mm:ss.SSSXXX",
                      )
                    """.trimIndent()
            )

            KotlinCode(
                code = """
                    
                    """.trimIndent()
            )

            //Now the usage is much cleaner and more concise
            //Improve Code, Fast coding, Fast Programming
            KotlinCode(
                code = """
                    //Best way
                    
                    //Static data
                    val list = remember {
                        mutableStateListOf<String>().apply {
                            for (i in 0..20) add(i)
                        }
                    }

                    //Empty List
                    sealed class ViewState {
                        data class ShowLoading(val businessList: BusinessListUiModel? = null) : ViewState()
                        data class ShowBusinessList(val businessList: BusinessListUiModel) : ViewState()
                    }
                    private val _uiState = MutableStateFlow<ViewState>(ViewState.ShowLoading())
                    val uiState = _uiState.asStateFlow()
                    _uiState.value = ViewState.ShowLoading(
                        businessList = (_uiState.value as? ViewState.ShowBusinessList)?.businessList
                    )

                    val items = mutableStateOf(persistentList0f<Item>())
                    fun addItem(item: Item) {
                        items.value = items.value.add(item) //Only recomposes if content changes
                    }
                    
                    private val _blogs = MutableStateFlow(BlogState())
                    val blogs = _blogs.asStateFlow()

                    _blogs.value = _blogs.value.copy(blogs = getAllBlog)
                    //_blogs.update { currentState -> currentState.copy(blogs = getAllBlog) }

                    //Best way
                    _blogs.update {
                        val update = it.blogs.toMutableList()
                        update.removeAt(0)
                        it.copy(blogs = update)
                    }

                    //removeIf
                    val cartList = _uiState.value.cartList.toMutableList()
                    cartList.removeIf { it.id == productId }

                    //update
                    val itemsCopy = _blogs.value.blogs.toMutableList()
                    itemsCopy[1].link = ""
                    _blogs.update { currentState -> currentState.copy(blogs = itemsCopy) }

                    //add no use apply
                    val itemsCopy = _blogs.value.blogs.toMutableList()
                    itemsCopy.add(Blog(1, "", "", "", ""))
                    _blogs.update { currentState -> currentState.copy(blogs = itemsCopy) }

                    //insert/add
                    val newList = mutableListOf<Blog>().apply { addAll(_blogs.value.blogs) }
                    newList.add(Blog(1, "", "", "", ""))
                    _blogs.value = _blogs.value.copy(blogs = newList)*/

                    //remove
                    val newList = mutableListOf<Blog>().apply { addAll(_blogs.value.blogs) }
                    newList.removeAt(0)
                    _blogs.value = _blogs.value.copy(blogs = newList)

                    //shuffed
                    _blogs.update { it.copy(blogs = it.blogs.shuffled(), isLoading = false) }

                    _blogs.update { it.copy(blogs = getAllBlog, isLoading = false) }

                    //Generic
                    private fun <T> MutableStateFlow<T>.setState(reducer: T.() -> T) {
                        this.value = this.value.reducer()
                    }

                    private val _uiState = MutableStateFlow(NewState())
                    public val uiState: MutableStateFlow<NewState> = _uiState

                    uiState.setState { copy(list store) }

                    //find id
                    private val _uiState = MutableStateFlow(ProductScreenUiState())
                    val uiState: StateFlow<ProductScreenUiState> = _uiState.asStateFlow()

                    _uiState.value.product?.id?.let { findProduct(1 = it) }

                    data class ProductScreenUiState(
                        val product: Product? = null
                    }

                    @Immutable
                    data class Product(
                        val id: Int
                    )

                    //Favourite list add
                    private val favoriteProducts = MutableStateFlow<List<Product>>(emptyList())

                    override suspend fun addToFavorites(uuid: String) {
                        val alreadyExist = favoriteProducts.value.any { it.uuid == uuid }
                        if (alreadyExist.not()) {
                            val product = fakeProducts.first { it.uuid == uuid }
                            val products = favoriteProducts.value.toMutableList().apply { add(product) }.toList()
                            favoriteProducts.emit(products)
                        }
                    }

                    //Delete favourite
                    override suspend fun deleteFromFavorites(uuid: String) {
                        val otherProducts = favoriteProducts.value.filter { it.uuid != uuid }
                        favoriteProducts.emit(otherProducts)
                    }

                    //SnapshotStateList Dynamic LazyColumn
                    private val _uiState = MutableStateFlow(CreateTaskScreenUIState())
                    val uiState = _uiState.asStateFlow()

                    fun onDone(value: String) {
                        _uiState.update {
                            it.copy(
                                checkList = uiState.value.checkList.apply { add(value) },
                                checkItem = ""
                            )
                        }
                    }

                    data class CreateTaskScreenUIState(
                        val checkList: SnapshotStateList<String> = mutableStateListOf()
                    )

                    //UI
                    uiState.checkList.removeAt(index)

                    //@Composable invoke
                    @Composable
                    private fun TaskManagementRow(
                        modifier: Modifier = Modifier,
                        header: (@Composable () -> Unit)? = null,
                        weeklyTasks: (LazyListScope.() -> Unit)? = null,
                        todayTasks: (LazyListScope.() -> Unit)? = null,
                    ) {
                        val state = rememberLazyListState()
                        LazyColumn(
                            state = state,
                            modifier = modifier
                                .background(BackgroundColor)
                                .fillMaxSize()
                    ) {
                            item {
                                header?.invoke()

                            }
                            weeklyTasks?.invoke(this)
                            todayTasks?.invoke(this)
                        }
                    }

                    //cachedIn (Pop-Corns code)
                    private val _filterState = MutableStateFlow(MovieFilter())
                    val filterState get() = _filterState.asStateFlow()

                    @OptIn(ExperimentalCoroutinesApi::class, FlowPreview::class)
                    val movies = filterState
                        .debounce(500)
                        .flatMapLatest { filter ->
                            getMovies(filter)
                        }.cachedIn(viewModelScope)

                    //UI
                    val movies = viewModel.movies.collectAsLazyPagingItems()

                    //typealias
                    typealias MutableTasks = MutableState<RequestState<List<ToDoTask>>>
                    typealias Tasks = MutableState<RequestState<List<ToDoTask>>>

                    private var _activeTasks: MutableTasks = mutableStateOf(RequestState.Idle)
                    val activeTasks: Tasks = _activeTasks

                     //functional interface click
                     fun interface InterfaceFunction {
                         fun onClick(): () -> Unit
                     }

                     @Composable
                     fun FunctionalInterfaceButton(type: InterfaceFunction) {
                         Button(onClick = type.onClick()) {
                             Text (text = "Functional Interface Button")
                         }
                     }

                     //filter/search
                     //https://github.com/enmanuel52/Pop-Corns
                     @OptIn(ExperimentalCoroutinesApi::class, FlowPreview::class)
                     val movies = filterState
                     .debounce(500)
                     .flatMapLatest { filter ->

                     }.cachedIn(viewModelScope)

                     //https://github.com/abdulhamidrpn/Top-News-KMP
                     private val viewModelScope = CoroutineScope(Dispatchers.IO)

                     private val _state = MutableStateFlow(HomeScreenState(Resource.Loading))
                     val state: StateFlow<HomeScreenState> = _state.stateIn(
                         scope = viewModelScope,
                         started = SharingStarted.WhileSubscribed(),
                         initialValue = HomeScreenState(),
                     )

                     //enum toTypedArray
                     enum class SpeciesFilterChips(val displayName: String, val classNames: List<String>) {
                          Mammal("Mammals", listOf("MAMMALIA")),
                          Bird("Birds", listOf("AVES")),
                          Amphibian("Amphibians", listOf("AMPHIBIA")),
                          Reptile("Reptiles", listOf("REPTILIA")),
                          Plants("Plants", listOf("MAGNOLIOPSIDA", "LILIOPSIDA"))
                     }
                     val chips = SpeciesFilterChips.entries.toTypedArray()

                      //moko mvvm
                      //https://github.com/ChromeSD22159/BauchGlueck
                      val isButtonEnabled: StateFlow<Boolean> = combine(mail, password) { username, password ->
                           username.isNotBlank() && password.isNotBlank()
                      }.stateIn(viewModelScope, SharingStarted.WhileSubscribed(5000), false).cStateFlow()
                      
                      //Random Color
                      fun getRandomColor(): Color {
                          return Color((155..255).random(), (155..255).random(), (155..255).random())
                      }
                    """.trimIndent()
            )

            KotlinCode(
                code = """
                    inline fun <reified T : Any> SavedStateHandle.getArgs(
                        typeMap: Map<KType, @JvmSuppressWildcards NavType<*>> = emptyMap()
                    ): T? = runCatching { toRoute<T>(typeMap = typeMap) }.getOrNull()
                    """.trimIndent()
            )

            KotlinCode(
                code = """
                    class CartViewModel(
                        private val repository: CartRepository
                    ) : ViewModel() {

                    private val _uiState = MutableStateFlow(CartUiState())
                        val uiState: StateFlow<CartUiState> = _uiState
                        init {
                            viewModelScope.launch {
                                val items = repository.loadCart()
                                _uiState.update { it.copy(items = items) }
                            }
                        }
                        fun onItemQuantityChanged(itemId: String, newQty: Int) {
                            _uiState.update { state ->
                                state.copy(
                                    items = state.items.map {
                                        if (it.id == itemId) it.copy(quantity = newQty) else it
                                    }
                                )
                            }
                        }
                    }
                    
                    data class CartUiState(
                        val items: List<CartItem> = emptyList()
                    )
                    """.trimIndent()
            )

            //https://github.com/bimovall/chess-player-viewer
            KotlinCode(
                code = """
                    //sealed class using update
                    
                    interface LeaderboardRepository {
                        fun getLeaderboard(): Flow<Result<Leaderboards>>
                    }
                    
                    class GetLeaderboardUseCase(private val repository: LeaderboardRepository) {
                        operator fun invoke(): Flow<Result<Leaderboards>> {
                            return repository.getLeaderboard()
                        }
                    }
                    
                    @Serializable
                    data class PlayerDto(
                        @SerialName("player_id") val playerId: Int,
                        @SerialName("@id") val id: String,
                        val url: String,
                        val username: String,
                        val score: Int,
                        val rank: Int,
                        val title: String? = null,
                        val name: String? = null,
                        val avatar: String
                    )
                    
                    data class Leaderboards(
                        val data: Map<String, List<Player>>
                    ) {
                        data class Player(
                            val playerId: Int,
                            val id: String,
                            val url: String,
                            val username: String,
                            val score: Int,
                            val rank: Int,
                            val name: String,
                            val title: String,
                            val avatar: String
                        )
                    }

                    fun mapToLeaderboards(map: Map<String, List<PlayerDto>>): Leaderboards {
                        val result = hashMapOf<String, List<Player>>()

                        for ((key, value) in map) {
                            result[key] = value.map { it.mapToPlayer() }
                        }

                        return Leaderboards(
                            result
                        )
                    }

                    fun PlayerDto.mapToPlayer(): Player {
                        return Player(
                            playerId = this.playerId,
                            id = this.id,
                            url = this.url,
                            username = this.username,
                            score = this.score,
                            rank = this.rank,
                            title = this.title ?: "",
                            name = this.name ?: "",
                            avatar = this.avatar
                        )
                    }
                    
                    sealed class LeaderboardUiState {
                        data object Loading : LeaderboardUiState()
                        data class Success(val leaderboards: Leaderboards, val error: ErrorHandler? = null, val selectedFilter: String = "daily") : LeaderboardUiState()
                        data class Error(val error: ErrorHandler) : LeaderboardUiState()
                    }
                    
                    class LeaderboardViewModel(val getLeaderboardUseCase: GetLeaderboardUseCase) : ViewModel() {

                        private var _uiState = MutableStateFlow<LeaderboardUiState>(LeaderboardUiState.Loading)
                        val uiState = _uiState.asStateFlow()

                        private var leaderboardHasCalled = false

                        fun getLeaderboards(filter: String = "daily") {
                            if (leaderboardHasCalled) return
                            leaderboardHasCalled = true
                            viewModelScope.launch {
                                getLeaderboardUseCase().collect { result ->
                                    _uiState.update { old ->
                                        when {
                                            result is Result.Success -> {
                                                LeaderboardUiState.Success(result.data, selectedFilter = filter)
                                            }

                                            result is Result.Error && old is LeaderboardUiState.Success -> {
                                                old.copy(error = result.cause)
                                            }

                                            result is Result.Error -> LeaderboardUiState.Error(result.cause)

                                            else -> old
                                        }
                                    }
                                }
                            }
                        }

                        fun updateFilter(filter: String) {
                            if (uiState.value is LeaderboardUiState.Success) {
                                _uiState.update {
                                    (_uiState.value as LeaderboardUiState.Success).copy(selectedFilter = filter)
                                }
                            }
                        }
                    }
                    """.trimIndent()
            )

            //https://github.com/bimovall/chess-player-viewer
            KotlinCode(
                code = """
                    //data class using update
                    
                    data class ProfileUiState(
                        val profileState: ProfileStatusState = ProfileStatusState.Loading,
                        val favoriteState: FavoriteState = FavoriteState.Loading,
                    )

                    sealed class ProfileStatusState {
                        data object Loading : ProfileStatusState()
                        data class Success(val profile: Profile, val stats: PlayerStats?) : ProfileStatusState()
                        data class ProfileError(val error: ErrorHandler) : ProfileStatusState()
                        data class PlayerStatsError(val error: ErrorHandler) : ProfileStatusState()
                    }

                    sealed class FavoriteState {
                        data object Loading : FavoriteState()
                        data class Success(val isDataAvailable: Boolean) : FavoriteState()
                        data class Error(val error: ErrorHandler) : FavoriteState()
                    }
                    
                    class ProfileViewModel(
                        private val getProfilePlayerStatsUseCase: GetProfilePlayerStatsUseCase
                    ) :
                        ViewModel() {

                        private var _uiState: MutableStateFlow<ProfileUiState> = MutableStateFlow(ProfileUiState())
                        val uiState = _uiState.asStateFlow()

                        fun getProfile(username: String) {
                            viewModelScope.launch {
                                _uiState.update {
                                    it.copy(profileState = ProfileStatusState.Loading)
                                }

                                getProfilePlayerStatsUseCase.invoke(username).collect { combined ->
                                    val profileResult = combined.profile
                                    val statsResult = combined.playerStats
                                    _uiState.update {
                                        it.copy(
                                            profileState = when {
                                                profileResult is Result.Success && statsResult is Result.Success -> {
                                                    ProfileStatusState.Success(
                                                        profile = profileResult.data,
                                                        stats = statsResult.data
                                                    )
                                                }

                                                profileResult is Result.Success -> {
                                                    ProfileStatusState.Success(
                                                        profile = profileResult.data,
                                                        stats = null
                                                    )
                                                }

                                                profileResult is Result.Error -> {
                                                    ProfileStatusState.ProfileError(profileResult.cause)
                                                }

                                                statsResult is Result.Error -> {
                                                    ProfileStatusState.PlayerStatsError(statsResult.cause)
                                                }

                                                else -> {
                                                    ProfileStatusState.Loading
                                                }
                                            }
                                        )
                                    }
                                }
                            }
                        }
                    }
                    """.trimIndent()
            )

            KotlinCode(
                code = $$"""
                    @file:OptIn(ExperimentalUuidApi::class)

                    import kotlin.uuid.ExperimentalUuidApi
                    import kotlin.uuid.Uuid

                    sealed class Request(val id: Uuid = Uuid.random())
                    
                    class OrderRequest(val amount: Int) : Request()
                    class RefundRequest(val amount: Int) : Request()
                    class SupportRequest(val text: String) : Request()

                    object FrontDesk {
                        fun receive(request: Request) {
                            when (request) {
                                is OrderRequest                         -> Warehouse.fulfillOrder(request)
                                is RefundRequest if request.amount < 10 -> println("Automatically refunded")
                                is RefundRequest                        -> Warehouse.fulfillRefund(request)
                                is SupportRequest                       -> HelpDesk.handle(request)
                            }
                        }
                    }

                    object Warehouse {
                        fun fulfillOrder(order: OrderRequest) = println("Fulfilling order #${order.id}")
                        fun fulfillRefund(refund: RefundRequest) = println("Fulfilling refund #${refund.id}")
                    }

                    object HelpDesk {
                        fun handle(request: SupportRequest) = println("Help desk is handling ${request.id}")
                    }

                    fun main() {
                        val request = RefundRequest(8)
                        FrontDesk.receive(request)
                    }
                    """.trimIndent()
            )

            //https://github.com/Joker-x-dev/CoolMallKotlin.git
            KotlinCode(
                code = """
                    //Add To Cart UI
                    //WrapRow (Row) Icon Set Horizontal
                    
                    //Row.kt
                    @Composable
                    fun WrapRow(
                        modifier: Modifier = Modifier,
                        horizontalArrangement: Arrangement.Horizontal = Arrangement.Start,
                        verticalAlignment: Alignment.Vertical = Alignment.CenterVertically,
                        padding: Dp = 0.dp,
                        content: @Composable RowScope.() -> Unit
                    ) {
                        Row(
                            modifier = modifier
                                .wrapContentWidth()
                                .let { if (padding > 0.dp) it.padding(padding) else it },
                            horizontalArrangement = horizontalArrangement,
                            verticalAlignment = verticalAlignment,
                            content = content
                        )
                    }
                    
                    //Icon.kt
                    @Composable
                    fun CommonIcon(
                        painter: Painter,
                        contentDescription: String? = null,
                        modifier: Modifier = Modifier,
                        size: Dp? = 24.dp,
                        tint: Color = LocalContentColor.current
                    ) {
                        val finalModifier = if (size != null) {
                            modifier.then(Modifier.size(size))
                        } else {
                            modifier
                        }

                        Icon(
                            painter = painter,
                            contentDescription = contentDescription,
                            modifier = finalModifier,
                            tint = tint
                        )
                    }
                    
                    //QuantityStepper.kt
                    @Composable
                    fun QuantityStepper(
                        quantity: Int,
                        minValue: Int = 1,
                        maxValue: Int = Int.MAX_VALUE,
                        onQuantityChanged: (Int) -> Unit,
                        buttonSize: Int = 24,
                        quantityWidth: Int = 24
                    ) {
                        WrapRow {
                            Box(
                                contentAlignment = Alignment.Center,
                                modifier = Modifier
                                    .size(buttonSize.dp)
                                    .clip(CircleShape)
                                    .background(
                                        color = MaterialTheme.colorScheme.surfaceVariant,
                                        shape = CircleShape
                                    )
                                    .clickable(enabled = quantity > minValue) {
                                        if (quantity > minValue) onQuantityChanged(quantity - 1)
                                    }
                            ) {
                                CommonIcon(
                                    resId = R.drawable.ic_reduce,
                                    tint = if (quantity > minValue)
                                        MaterialTheme.colorScheme.onSurface.copy(alpha = 0.6f)
                                    else
                                        MaterialTheme.colorScheme.onSurface.copy(alpha = 0.3f),
                                    size = 16.dp
                                )

                            }

                            Text(
                                text = quantity.toString(),
                                style = MaterialTheme.typography.bodyLarge,
                                modifier = Modifier
                                    .padding(horizontal = SpacePaddingMedium)
                                    .widthIn(min = quantityWidth.dp),
                                textAlign = TextAlign.Center
                            )

                            Box(
                                contentAlignment = Alignment.Center,
                                modifier = Modifier
                                    .size(buttonSize.dp)
                                    .clip(CircleShape)
                                    .background(
                                        color = Primary,
                                        shape = CircleShape
                                    )
                                    .clickable(enabled = quantity < maxValue) {
                                        if (quantity < maxValue) onQuantityChanged(quantity + 1)
                                    }
                            ) {
                                CommonIcon(
                                    resId = R.drawable.ic_add,
                                    tint = Color.White,
                                    size = 16.dp
                                )
                            }
                        }
                    }

                    @Preview(showBackground = true)
                    @Composable
                    private fun QuantityStepperPreview() {
                        AppTheme {
                            QuantityStepper(
                                quantity = 1,
                                onQuantityChanged = { }
                            )
                        }
                    } 
                    
                    //LazyColumn ItemView
                    @Composable
                    fun OrderGoodsItem(
                        modifier: Modifier = Modifier,
                        onQuantityChanged: (Int) -> Unit = {},
                    ) {
                        QuantityStepper(
                            quantity = ,
                            onQuantityChanged = onQuantityChanged
                        )
                    }
                    
                    @Composable
                    fun OrderGoodsCard(
                        modifier: Modifier = Modifier,
                        onQuantityChanged: (Long, Int) -> Unit = { _, _, ->},
                        enableQuantityStepper: Boolean = true
                    ) {
                        Card(modifier = modifier) {
                            data.spec.forEach { item ->
                                val isSpecDeleting = deletingSpecIds.contains(item.id)

                                AnimatedVisibility(
                                    visible = !isSpecDeleting,
                                    exit = slideOutHorizontally(
                                        targetOffsetX = { -it },
                                        animationSpec = tween(300)
                                    ) + fadeOut(animationSpec = tween(300))
                                ) {
                                    OrderGoodsItem(
                                        onQuantityChanged = { newCount -> onQuantityChanged(item.id, newCount) }
                                    )
                                }
                            }
                        }
                    }
                    """.trimIndent()
            )

            KotlinCode(
                code = $$"""
                    //Add To Cart
                    data class CoffeeMachineUiState(
                        val selectedSize: String = "Medium",
                        val quantity: Int = 1,
                        val cartQuantity: Int = 0,
                        val selectedCupDesign: Int = 0,
                        val fillState: FillState = FillState.Idle,
                        val fillProgress: Float = 0f,
                        val isBrewingAnimationActive: Boolean = false,
                        val price: String = "€3.00"
                    )
                    
                    class CoffeeMachineViewModel : ViewModel() {
                        private val _uiState = MutableStateFlow(CoffeeMachineUiState())
                        val uiState: StateFlow<CoffeeMachineUiState> = _uiState.asStateFlow()
                        
                        private fun calculatePrice(size: String, quantity: Int): String {
                            val basePrice = cupPrices[size] ?: 3.0
                            val totalPrice = basePrice * quantity
                            return "${totalPrice.toInt()}.${((totalPrice * 100).toInt() % 100).toString().padStart(2, '0')}"
                        }
                        
                        fun incrementQuantity() {
                            // Only allow quantity changes in Idle state
                            if (_uiState.value.fillState == FillState.Idle) {
                                val newQuantity = _uiState.value.quantity + 1
                                _uiState.value = _uiState.value.copy(
                                    quantity = newQuantity,
                                    price = calculatePrice(_uiState.value.selectedSize, newQuantity)
                                )
                            }
                        }
                    }
                    
                    @Composable
                    fun CoffeeMachineScreen(
                        viewModel: CoffeeMachineViewModel = remember { CoffeeMachineViewModel() }
                    ) { }
                    
                    //calculatedPercent
                    val hasDiscount = (originalPrice != null && discountedPrice != null && discountedPrice < originalPrice)
                     
                    val calculatedPercent = if (hasDiscount && originalPrice > 0) {
                        val reduction = originalPrice - discountedPrice
                            ((reduction / originalPrice) * 100)
                        } else {
                            0
                    }
                    """.trimIndent()
            )

            KotlinCode(
                code = """
                    fun Modifier.showIf(condition: Boolean): Modifier {
                        return if (condition) this else Modifier.size(0.dp)
                    }

                    fun Modifier.animateVisibility(isVisible: Boolean): Modifier {
                        return if (isVisible) {
                            this.alpha(1f)
                        } else {
                            this.alpha(0f)
                        }
                    }
                    """.trimIndent()
            )

            //Navigation
            KotlinCode(
                code = """
                    fun <T : Any> NavHostController.navigateSingleTopTo(route: T) {
                        navigate(route) {
                            popUpTo(
                                this@navigateSingleTopTo.graph.findStartDestination().id
                            ) {
                                saveState = true
                            }
                            launchSingleTop = true
                            restoreState = true
                        }
                    }
                    """.trimIndent()
            )

            KotlinCode(
                code =
                    """
                        //DateTransformation
                        private class DateTransformation() : VisualTransformation {
                            override fun filter(text: AnnotatedString): TransformedText {
                                return dateFilter(text)
                            }

                            private fun dateFilter(text: AnnotatedString): TransformedText {
                                val trimmed = if (text.text.length >= 8) text.text.substring(0..7) else text.text
                                var out = ""
                                for (i in trimmed.indices) {
                                    out += trimmed[i]
                                    if (i == 3 || i == 5) out += "/"
                                }

                                val numberOffsetTranslator = object : OffsetMapping {
                                    override fun originalToTransformed(offset: Int): Int {
                                        if (offset <= 4) return offset
                                        if (offset <= 5) return offset + 1
                                        if (offset <= 8) return offset + 2
                                        return 10
                                    }

                                    override fun transformedToOriginal(offset: Int): Int {
                                        if (offset <= 2) return offset
                                        if (offset <= 5) return offset - 1
                                        if (offset <= 10) return offset - 2
                                        return 8
                                    }
                                }

                                return TransformedText(AnnotatedString(out), numberOffsetTranslator)
                            }
                        }
                        """.trimIndent()
            )

            //libs.versions.toml file using version set
            KotlinCode(
                code = $$"""
                    //libs.versions.toml file using version set

                    android {
                        defaultConfig {
                            applicationId = libs.versions.applicationId.get()
                            versionCode =
                                libs.versions.versionMajor.get().toInt() * 1000 + libs.versions.versionMinor.get()
                                    .toInt() * 100 + libs.versions.versionPatch.get().toInt()
                            versionName =
                                "${libs.versions.versionMajor.get()}.${libs.versions.versionMinor.get()}.${libs.versions.versionPatch.get()}"
                            testInstrumentationRunner = "com.ngapp.metanmobile.core.testing.MetanMobileTestRunner"
                            vectorDrawables {
                                useSupportLibrary = true
                            }
                        }
                    }

                    //libs.versions.toml
                    [versions]
                    androidCompileSdk = "34"
                    androidMinSdk = "21"
                    androidTargetSdk = "34"
                    versionMajor = "2"
                    versionMinor = "1"
                    versionPatch = "11"
                    applicationId = "com.ngapp.metanmobile"
                    """.trimIndent()
            )

            //Improve Code Clean Architecture
            SpanText(
                text = "UseCase Clean Architecture",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )
            KotlinCode(
                code = """
                    interface UseCase<out Type, in Params> where Type: Any {
                        suspend operator fun invoke(params: Params): Result<Type>
                    }
                    """.trimIndent()
            )

            //Improve Code Clean Architecture (BASE CLASS - Handle Response, Network Error, ViewModel)
            SpanText(
                text = "BASE CLASS - Handle Response, Network Error, ViewModel",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            //https://github.com/OmarLkhalil/ValorantUI.git
            KotlinCode(
                code = """
                    import kotlinx.coroutines.CoroutineScope
                    import kotlinx.coroutines.Dispatchers
                    import kotlinx.coroutines.flow.Flow
                    import kotlinx.coroutines.flow.MutableSharedFlow
                    import kotlinx.coroutines.flow.MutableStateFlow
                    import kotlinx.coroutines.flow.StateFlow
                    import kotlinx.coroutines.flow.asStateFlow
                    import kotlinx.coroutines.launch
                    import org.koin.core.component.KoinComponent

                    abstract class BaseViewModel<I, S : UiState>(
                        initialState: S
                    ) : KoinComponent{
                        private val _state = MutableStateFlow(initialState)
                        val state: StateFlow<S> = _state.asStateFlow()
                        private val intentFlow = MutableSharedFlow<I>()
                        private val viewModelScope = CoroutineScope(Dispatchers.Default)
                        init {
                            viewModelScope.launch {
                                intentFlow.collect { intent ->
                                    handleIntent(intent)
                                }
                            }
                        }
                        fun sendIntent(intent: I) {
                            viewModelScope.launch { intentFlow.emit(intent) }
                        }
                        protected fun setState(reducer: S.() -> S) {
                            _state.value = _state.value.reducer()
                        }
                        protected suspend fun <T> collectDataState(
                            flow: Flow<DataState<T>>,
                            onSuccess: (T) -> Unit
                        ) {
                            flow.collect { dataState ->
                                when (dataState) {
                                    is DataState.Loading -> onLoading()
                                    is DataState.Error -> onError(dataState.exception)
                                    is DataState.Success -> {
                                        onSuccess(dataState.data)
                                    }
                                    DataState.Idle -> onIdle()
                                }
                            }
                        }
                        protected open suspend fun handleIntent(intent: I) {
                        }
                        protected open fun onLoading() {}
                        protected open fun onError(e: Throwable) {}
                        protected open fun onIdle() {}
                    }
                    
                    interface UiState {
                        val isLoading: Boolean
                        val error: String?
                    }
                    
                    """.trimIndent()
            )

            KotlinCode(
                code = """
                    //https://github.com/ngapp-dev/Quottie-KMP

                    //StateBaseViewModel
                    //StateBaseViewModel<SearchUiState, SearchAction, SearchEvent>()
                    abstract class StateBaseViewModel<T : BaseUiState, A : BaseAction, E : BaseEvent> : ViewModel() {

                        protected val eventChannel = Channel<E>()
                        val events: Flow<E> = eventChannel.receiveAsFlow()

                        abstract fun triggerAction(action: A)

                        protected fun safeLaunch(
                            dispatcher: CoroutineContext = EmptyCoroutineContext,
                            block: suspend CoroutineScope.() -> Unit
                        ) {
                            viewModelScope.launch(context = dispatcher, block = block)
                        }
                    }

                    //BaseViewModel.kt
                    abstract class BaseViewModel<T : BaseUiState, A : BaseAction, E : BaseEvent> :
                        ViewModel() {

                        abstract fun provideUiState(): T

                        protected val _uiState by lazy { MutableStateFlow(provideUiState()) }
                        val uiState = _uiState.asStateFlow()

                        protected val eventChannel by lazy { Channel<BaseEvent>() }
                        val events = eventChannel.receiveAsFlow()

                        abstract fun triggerAction(action: A)

                        protected fun safeLaunch(
                            dispatcher: CoroutineContext = EmptyCoroutineContext,
                            block: suspend CoroutineScope.() -> Unit
                        ) {
                            viewModelScope.launch(context = dispatcher, block = block)
                        }
                    }

                    interface BaseUiState
                    interface BaseAction
                    interface BaseEvent

                    //HandleError.kt
                    fun <T> handleError(exception: Exception): Result<T, DataError.Network> {
                        return when (exception) {
                            is UnresolvedAddressException -> Result.Error(DataError.Network.NO_INTERNET, exception)
                            is SerializationException -> Result.Error(DataError.Network.SERIALIZATION, exception)
                            is SocketTimeoutException -> Result.Error(DataError.Network.SERVER_ERROR, exception)
                            is IOException -> Result.Error(DataError.Network.SERVER_ERROR, exception)
                            else -> Result.Error(DataError.Network.UNKNOWN, exception)
                        }
                    }

                    //HandleResponse.kt
                    suspend fun <T> handleResponse(
                        response: HttpResponse,
                        parse: suspend (HttpResponse) -> T
                    ): Result<T, DataError.Network> {
                        return when (response.status.value) {
                            in 200..299 -> {
                                try {
                                    val result = parse(response)
                                    Result.Success(result)
                                } catch (e: Exception) {
                                    Result.Error(DataError.Network.UNKNOWN, e)
                                }
                            }
                            401 -> Result.Error(DataError.Network.AUTHORIZATION_ERROR, Exception("Authorization error"))
                            409 -> Result.Error(DataError.Network.CONFLICT, Exception("Conflict error"))
                            408 -> Result.Error(DataError.Network.REQUEST_TIMEOUT, Exception("Request timeout"))
                            413 -> Result.Error(DataError.Network.PAYLOAD_TOO_LARGE, Exception("Payload too large"))
                            in 500..599 -> Result.Error(DataError.Network.SERVER_ERROR, Exception("Server error"))
                            else -> Result.Error(DataError.Network.UNKNOWN, Exception("Unknown error"))
                        }
                    }

                    //DataError.kt
                    sealed interface DataError: Error {
                        enum class Network: DataError {
                            BAD_REQUEST,
                            AUTHORIZATION_ERROR,
                            NOT_FOUND,
                            FORBIDDEN,
                            REQUEST_TIMEOUT,
                            TOO_MANY_REQUESTS,
                            NO_INTERNET,
                            PAYLOAD_TOO_LARGE,
                            SERVER_ERROR,
                            SERIALIZATION,
                            CONFLICT,
                            UNKNOWN
                        }
                        enum class Local: DataError {
                            DISK_FULL
                        }
                    }

                    //Error.kt
                    sealed interface Error

                    //Result.kt
                    typealias RootError = Error

                    sealed interface Result<out D, out E : RootError> {
                        data class Success<out D, out E : RootError>(val data: D) : Result<D, E>
                        data class Error<out D, out E : RootError>(val error: E, val exception: Exception, val message: String? = null) :
                            Result<D, E>
                    }

                    //GithubNetworkDataSource.kt
                    interface GithubNetworkDataSource {
                        suspend fun getGithubUser(): HttpResponse
                    }

                    //GithubNetwork.kt
                    internal class GithubNetwork(
                        private val baseUrl: String,
                        private val httpClient: HttpClient
                    ) : GithubNetworkDataSource {

                        override suspend fun getGithubUser(): HttpResponse {
                            return httpClient.get("")
                        }
                    }

                    //data/repository
                    interface GithubUserRepository {
                        suspend fun getGithubUser(): Result<GithubUserResource, DataError.Network>
                    }

                    //data/repository
                    class DefaultGithubUserRepository(
                        val githubNetwork: GithubNetworkDataSource,
                    ) : GithubUserRepository {

                        override suspend fun getGithubUser(): Result<GithubUserResource, DataError.Network> =
                            try {
                                val response = githubNetwork.getGithubUser()
                                val result = handleResponse(response) { it.body<NetworkGithubUser>() }
                                if (result is Result.Success) {
                                    Result.Success(result.data.asResource())
                                } else {
                                    Result.Error((result as Result.Error).error, result.exception)
                                }
                            } catch (e: Exception) {
                                handleError(e)
                            }
                    }

                    //model
                    data class GithubUserResource(
                        val id: Int,
                        val login: String,
                        val avatarUrl: String,
                        val url: String,
                        val htmlUrl: String,
                        val name: String,
                        val company: String,
                        val blog: String,
                        val location: String,
                        val email: String,
                        val bio: String,
                        val twitterUsername: String,
                    ) {
                        companion object {
                            fun init() = GithubUserResource(
                                id = 49076456,
                                login = "ngapp-dev",
                                avatarUrl = "https://avatars.githubusercontent.com/u/49076456?v=4",
                                url = "https://api.github.com/users/ngapp-dev",
                                htmlUrl = "https://github.com/ngapp-dev",
                                name = "NGApps Dev",
                                company = "",
                                blog = "https://metan.by/",
                                location = "Poznan",
                                email = "",
                                bio = "",
                                twitterUsername = "",
                            )
                        }
                    }

                    fun NetworkGithubUser.asResource() = GithubUserResource(
                        id = id,
                        login = login.orEmpty(),
                        avatarUrl = avatarUrl.orEmpty(),
                        url = url.orEmpty(),
                        htmlUrl = htmlUrl.orEmpty(),
                        name = name.orEmpty(),
                        company = company.orEmpty(),
                        blog = blog.orEmpty(),
                        location = location,
                        email = email.orEmpty(),
                        bio = bio.orEmpty(),
                        twitterUsername = twitterUsername.orEmpty()
                    )

                    @Serializable
                    data class NetworkAuthor(
                        @SerialName(value = "_id") val id: String,
                        val bio: String = "",
                        val description: String? = "",
                        val link: String = "",
                        val name: String = "",
                        val slug: String = "",
                        val quoteCount: Int = 0,
                    )
                    """.trimIndent()
            )

            KotlinCode(
                code = """
                    //https://github.com/RuslanBarcho/Vintrless

                    abstract class BaseViewModel(
                        protected val navigator: AppNavigator
                    ) : ViewModel(), CoroutineScope {

                        private val job = SupervisorJob()

                        override val coroutineContext = Dispatchers.Main + job

                        // private val primaryLoaderInteractor: PrimaryLoaderInteractor by inject()

                        private val resultHandlerListeners: MutableMap<String, ResultListenerHandler> = mutableMapOf()

                        open fun navigateBack(type: NavigatorType? = null) { navigator.back(type) }

                        protected fun createExceptionHandler(
                            onException: (Throwable) -> Unit = { }
                        ) = CoroutineExceptionHandler { _, throwable ->
                            throwable.printStackTrace()
                            onException.invoke(throwable)
                        }

                        protected fun <T> MutableStateFlow<BaseScreenState<T>>.loadWithStateHandling(
                            emptyCheckAction: (T) -> Boolean = { _ -> false },
                            block: suspend () -> T,
                        ) = launch(createExceptionHandler { value = BaseScreenState.Error() }) {
                            value = BaseScreenState.Loading()
                            val data = block()

                            value = if (emptyCheckAction(data)) {
                                BaseScreenState.Empty()
                            } else {
                                BaseScreenState.Loaded(data)
                            }
                        }

                        protected fun <T> MutableStateFlow<BaseScreenState<T>>.refreshWithStateHandling(
                            block: suspend () -> T
                        ) = launch(createExceptionHandler { value = BaseScreenState.Error() }) {
                            val lockedValue = value

                            if (lockedValue is BaseScreenState.Loaded) {
                                value = BaseScreenState.Loaded(lockedValue.payload, isRefreshing = true)
                                value = BaseScreenState.Loaded(block())
                            }
                        }

                        suspend fun withLoading(
                            setLoadingCallback: (Boolean) -> Unit,
                            showLoading: Boolean = true,
                            action: suspend () -> Unit,
                        ) {
                            try {
                                if (showLoading) {
                                    setLoadingCallback(true)
                                }
                                action()
                                if (showLoading) {
                                    setLoadingCallback(false)
                                }
                            } catch (e: Throwable) {
                                setLoadingCallback(false)
                                throw e
                            }
                        }

                        suspend fun withPrimaryLoader(action: suspend () -> Unit) {
                            withLoading(
                                setLoadingCallback = { isLoading ->
                                    // primaryLoaderInteractor.setLoaderState(isLoading)
                                },
                                action = action
                            )
                        }

                        protected fun <T> Flow<T>.stateInThis(initialValue: T) = stateIn(
                            scope = this@BaseViewModel,
                            started = SharingStarted.Lazily,
                            initialValue = initialValue,
                        )

                        protected fun handleResult(key: String, block: () -> ResultListenerHandler) {
                            resultHandlerListeners += key to block()
                        }

                        override fun onCleared() {
                            // Cancel all suspend operations
                            if (isActive) cancel()

                            // Clear all result handlers
                            resultHandlerListeners.forEach { it.value.dispose() }
                            resultHandlerListeners.clear()

                            super.onCleared()
                        }
                    }

                    interface BaseScreenState<T> {

                        val isLoaded: Boolean
                            get() = this is Loaded

                        class Loading<T> : BaseScreenState<T>

                        class Error<T> : BaseScreenState<T>

                        class Empty<T> : BaseScreenState<T>

                        data class Loaded<T>(
                            val payload: T,
                            val isRefreshing: Boolean = false,
                        ) : BaseScreenState<T>
                    }

                    @Composable
                    fun <T> ScreenStateLayout(
                        modifier: Modifier = Modifier,
                        state: BaseScreenState<T>,
                        errorRetryAction: () -> Unit = {},
                        loading: @Composable () -> Unit = {
                            Box(
                                modifier = Modifier.fillMaxSize(),
                                contentAlignment = Alignment.Center
                            ) {
                                CircularProgressIndicator(
                                    modifier = Modifier.size(36.dp),
                                    color = VintrlessExtendedTheme.colors.navBarSelected,
                                )
                            }
                        },
                        error: @Composable () -> Unit = {},
                        empty: @Composable () -> Unit = {},
                        other: @Composable (BaseScreenState<T>) -> Unit = {},
                        loaded: @Composable (BaseScreenState.Loaded<T>) -> Unit,
                    ) {
                        Box(modifier = modifier) {
                            when (state) {
                                is BaseScreenState.Loading -> {
                                    loading()
                                }
                                is BaseScreenState.Error -> {
                                    error()
                                }
                                is BaseScreenState.Loaded -> {
                                    loaded(state)
                                }
                                is BaseScreenState.Empty -> {
                                    empty()
                                }
                                else -> {
                                    other(state)
                                }
                            }
                        }
                    }

                    fun interface ResultListener {
                        fun onResult(data: Any)
                    }

                    /**
                     * Handler for manual delete subscription and avoid leak
                     */
                    fun interface ResultListenerHandler {
                        fun dispose()
                    }

                    class ResultWire {
                        private val listeners = mutableMapOf<String, ResultListener>()

                        fun setResultListener(key: String, listener: ResultListener): ResultListenerHandler {
                            listeners[key] = listener
                            return ResultListenerHandler {
                                listeners.remove(key)
                            }
                        }

                        fun sendResult(key: String, data: Any) {
                            listeners.remove(key)?.onResult(data)
                        }

                        fun removeListener(key: String) {
                            listeners.remove(key)
                        }
                    }

                    private const val NAVIGATION_EFFECT_KEY = "navigation"

                    class AppNavigator {

                        private val _actionFlow = MutableSharedFlow<NavigatorAction>(extraBufferCapacity = 10)

                        private val resultWire = ResultWire()

                        private var currentNavigatorType: NavigatorType = NavigatorType.Root

                        val actionFlow = _actionFlow.asSharedFlow()

                        fun switchNavigatorType(type: NavigatorType) {
                            currentNavigatorType = type
                        }

                        fun back(type: NavigatorType? = null) {
                            _actionFlow.tryEmit(
                                NavigatorAction.Back(
                                    navigatorType = type ?: currentNavigatorType
                                )
                            )
                        }

                        fun <T: Any> back(type: NavigatorType? = null, resultKey: String, result: T? = null) {
                            _actionFlow.tryEmit(
                                NavigatorAction.Back(
                                    navigatorType = type ?: currentNavigatorType
                                )
                            )

                            result
                                ?.let { resultWire.sendResult(resultKey, it) }
                                ?: run { resultWire.removeListener(resultKey) }
                        }

                        fun backToStart(type: NavigatorType? = null) {
                            _actionFlow.tryEmit(
                                NavigatorAction.BackToStart(
                                    navigatorType = type ?: currentNavigatorType
                                )
                            )
                        }

                        fun forward(
                            screen: Screen,
                            type: NavigatorType? = null
                        ) {
                            _actionFlow.tryEmit(
                                NavigatorAction.Forward(
                                    screen = screen,
                                    navigatorType = type ?: currentNavigatorType,
                                )
                            )
                        }

                        @Suppress("UNCHECKED_CAST")
                        fun <T> forwardWithResult(
                            screen: Screen,
                            type: NavigatorType? = null,
                            resultKey: String,
                            resultCallback: (T) -> Unit
                        ): ResultListenerHandler {
                            _actionFlow.tryEmit(
                                NavigatorAction.Forward(
                                    screen = screen,
                                    navigatorType = type ?: currentNavigatorType,
                                )
                            )

                            return resultWire.setResultListener(resultKey) { resultCallback(it as T) }
                        }

                        fun replace(
                            screen: Screen,
                            type: NavigatorType? = null,
                            applyNavigatorType: Boolean = false
                        ) {
                            _actionFlow.tryEmit(
                                NavigatorAction.Replace(
                                    screen = screen,
                                    navigatorType = type ?: currentNavigatorType,
                                )
                            )

                            if (applyNavigatorType && type != null) {
                                currentNavigatorType = type
                            }
                        }

                        fun replaceAll(
                            screen: Screen,
                            type: NavigatorType? = null,
                            applyNavigatorType: Boolean = false
                        ) {
                            _actionFlow.tryEmit(
                                NavigatorAction.ReplaceAll(
                                    screen = screen,
                                    navigatorType = type ?: currentNavigatorType,
                                )
                            )

                            if (applyNavigatorType && type != null) {
                                currentNavigatorType = type
                            }
                        }
                    }

                    sealed class NavigatorAction {

                        abstract val navigatorType: NavigatorType

                        data class Back(
                            override val navigatorType: NavigatorType
                        ) : NavigatorAction()

                        data class BackToStart(
                            override val navigatorType: NavigatorType
                        ) : NavigatorAction()

                        data class Forward(
                            val screen: Screen,
                            override val navigatorType: NavigatorType,
                        ) : NavigatorAction()

                        data class Replace(
                            val screen: Screen,
                            override val navigatorType: NavigatorType,
                        ) : NavigatorAction()

                        data class ReplaceAll(
                            val screen: Screen,
                            override val navigatorType: NavigatorType,
                        ) : NavigatorAction()
                    }

                    interface NavigatorType {
                        object Root : NavigatorType
                    }

                    @Composable
                    fun NavigatorEffect(
                        type: NavigatorType,
                        navigator: AppNavigator,
                        controller: NavController,
                        onCustomCommand: (NavigatorAction) -> Unit = {},
                    ) {
                        LaunchedEffect(NAVIGATION_EFFECT_KEY) {
                            navigator.actionFlow.onEach { action ->
                                if (action.navigatorType == type) {
                                    when (action) {
                                        is NavigatorAction.Back -> {
                                            controller.navigateUp()
                                        }
                                        is NavigatorAction.BackToStart -> {
                                            controller.graph.findStartDestination().route?.let {
                                                controller.popBackStack(it, inclusive = false)
                                            }
                                        }
                                        is NavigatorAction.Forward -> {
                                            controller.navigate(action.screen)
                                        }
                                        is NavigatorAction.Replace -> {
                                            controller.navigate(action.screen) {
                                                popUpTo(controller.currentBackStack.value.last().id) {
                                                    inclusive = true
                                                }
                                            }
                                        }
                                        is NavigatorAction.ReplaceAll -> {
                                            controller.navigate(action.screen) { popUpToTop(controller) }
                                        }
                                        else -> {
                                            onCustomCommand(action)
                                        }
                                    }
                                }
                            }.launchIn(scope = this)
                        }
                    }

                    fun NavOptionsBuilder.popUpToTop(navController: NavController) {
                        popUpTo(navController.graph.id) {
                            inclusive =  true
                        }
                    }
                    """.trimIndent()
            )

            KotlinCode(
                code = """
                    //https://github.com/steve1rm/BusbyMoviesV2

                    sealed interface CheckResult<out D, out E, out T> {
                        data class Success<D>(val data: D)
                            : CheckResult<D, Nothing, Nothing>

                        data class Failure<out E: Error, T>(val exceptionError: Error, val responseError: T? = null)
                            : CheckResult<Nothing, E, T>
                    }

                    inline fun <D, E: Error, T> CheckResult<D, E, T>.onSuccess(block: (D) -> Unit): CheckResult<D, E, T> {
                        return when(this) {
                            is CheckResult.Success -> {
                                block(this.data)
                                this
                            }
                            else -> {
                                this
                            }
                        }
                    }

                    inline fun <D, E: Error, T> CheckResult<D, E, T>.onFailure(block: (Error, T?) -> Unit): CheckResult<D, E, T> {
                        return when(this) {
                            is CheckResult.Failure -> {
                                block(this.exceptionError, this.responseError)
                                this
                            }
                            else -> {
                                this
                            }
                        }
                    }
                    """.trimIndent()
            )

            KotlinCode(
                code = """
                    //Add to Cart logic / Change Quantity

                    data class CartScreenUIState(
                        val isLoading: Boolean = true,
                        val cartItems: MutableList<CartItem> = mutableListOf()
                    )

                    data class CartItem(
                        val product: String,
                        val quantity: Int,
                        val id: String,
                        val discount: Number?,
                        val price: Number,
                        val isFav: Boolean,
                        val image: String
                    )

                    private val _uiState = MutableStateFlow(CartScreenUIState())
                    val uiState = _uiState.asStateFlow()

                    fun onChangeQuantity(productID: String, isIncrease: Boolean) {
                        val item = uiState.value.cartItems.find { it.id == productID }

                        //minium 1 quantity
                        item?.let {
                            if (it.quantity == 1 && !isIncrease){
                                return
                            }
                        }

                        val newList = uiState.value.cartItems.map {
                            it.copy(
                                quantity = if (it.id == productID) {
                                    if (isIncrease) {
                                        it.quantity + 1
                                    } else {
                                        it.quantity - 1
                                    }
                                } else {
                                    it.quantity
                                }
                            )
                        }
                        _uiState.update { it.copy(cartItems = newList.toMutableList()) }
                    }

                    //Extension Function
                    fun List<CartItem>.calcItemsPrice(): Float {
                        var itemsPrice = 0.0f
                        this.forEach {
                            itemsPrice += (it.discount ?: it.price).toFloat() * it.quantity
                        }
                        return itemsPrice
                    }

                    //UI
                    cartItems.calcItemsPrice()
                    """.trimIndent()
            )

            KotlinCode(
                code = """
                     //Filter 
                                         
                     class FestivalMapViewModel : ViewModel() {
                         private var hasLoadedInitialData = false

                         private val _state = MutableStateFlow(FestivalMapState())
                         val state = _state
                             .onStart {
                                 if (!hasLoadedInitialData) {
                                     loadInitialData()

                                     hasLoadedInitialData = true
                                 }
                             }
                             .stateIn(
                                 scope = viewModelScope,
                                 started = SharingStarted.WhileSubscribed(5_000L),
                                 initialValue = FestivalMapState()
                             )

                         private fun loadInitialData() {
                             viewModelScope.launch {
                                 val filters = listOf(
                                     Filter(
                                         id = 0,
                                         iconRes = R.drawable.ic_stage,
                                         title = "Stages",
                                         background = FestivalMapColors.lime,
                                         selected = false
                                     ),
                                     Filter(
                                         id = 1,
                                         iconRes = R.drawable.ic_food,
                                         title = "Food",
                                         background = FestivalMapColors.pink,
                                         selected = false
                                     ),
                                     Filter(
                                         id = 2,
                                         iconRes = R.drawable.ic_wc,
                                         title = "WC",
                                         background = FestivalMapColors.orange,
                                         selected = false
                                     ),
                                 )

                                 val items = listOf(
                                     Item(filterId = 0, itemImgRes = R.drawable.indc_stages),
                                     Item(filterId = 1, itemImgRes = R.drawable.indc_food),
                                     Item(filterId = 2, itemImgRes = R.drawable.indc_wc),
                                 )

                                 _state.update { state ->
                                     state.copy(
                                         filters = filters,
                                         items = items
                                     )
                                 }
                             }
                         }

                         fun onAction(action: FestivalMapAction) {
                             when (action) {
                                 is FestivalMapAction.OnFilterToggle -> {
                                     toggleFilter(action.filter)
                                 }
                             }
                         }

                         private fun toggleFilter(selectedItem: Filter) {
                             _state.update {
                                 it.copy(
                                     filters = it.filters.map { filter ->
                                         if (filter.id == selectedItem.id) {
                                             filter.copy(selected = !filter.selected)
                                         } else filter
                                     }
                                 )
                             }
                         }
                     }
                     
                     data class FestivalMapState(
                         val filters: List<Filter> = emptyList(),
                         val items: List<Item> = emptyList(),
                     )

                     data class Filter(
                         val id: Int,
                         @param:DrawableRes val iconRes: Int,
                         val title: String,
                         val background: Color,
                         val selected: Boolean = false,
                     )

                     data class Item(
                         val filterId: Int,
                         @param:DrawableRes val itemImgRes: Int,
                     )
                     
                     sealed interface FestivalMapAction {
                         data class OnFilterToggle(val filter: Filter) : FestivalMapAction
                     }
                """.trimIndent()
            )

            KotlinCode(
                code = """
                    class TicketBuilderViewModel : ViewModel() {

                        private var hasLoadedInitialData = false

                        private val _state = MutableStateFlow(TicketBuilderState())
                        val state = _state
                            .onStart {
                                if (!hasLoadedInitialData) {
                                    loadInitialData()

                                    hasLoadedInitialData = true
                                }
                            }
                            .stateIn(
                                scope = viewModelScope,
                                started = SharingStarted.WhileSubscribed(5_000L),
                                initialValue = TicketBuilderState()
                            )

                        private fun loadInitialData() {
                            viewModelScope.launch {
                                val tickets = listOf(
                                    Ticket(
                                        title = "Standard",
                                        price = 40f,
                                        ticketType = TicketType.STANDART
                                    ),
                                    Ticket(
                                        title = "VIP",
                                        price = 70f,
                                        ticketType = TicketType.VIP
                                    ),
                                    Ticket(
                                        title = "Backstage",
                                        price = 120f,
                                        ticketType = TicketType.BACKSTAGE
                                    )
                                )

                                _state.update {
                                    it.copy(
                                        tickets = tickets,
                                        selectedTicketType = null,
                                        quantity = 1,
                                        total = 0f
                                    )
                                }
                            }
                        }

                        fun onAction(action: TicketBuilderAction) {
                            when (action) {
                                is TicketBuilderAction.OnTicketTypeSelected -> {
                                    _state.update {
                                        it.copy(
                                            selectedTicketType = action.ticket,
                                            quantity = 1,
                                            total = 0f
                                        )
                                    }

                                    calculateTotal()
                                }

                                TicketBuilderAction.OnQuantityDecrease -> {
                                    _state.update { it.copy(quantity = it.quantity - 1) }

                                    calculateTotal()
                                }

                                TicketBuilderAction.OnQuantityIncrease -> {
                                    _state.update { it.copy(quantity = it.quantity + 1) }

                                    calculateTotal()
                                }
                            }
                        }

                        private fun calculateTotal() {
                            viewModelScope.launch {
                                val state = _state.value
                                state.selectedTicketType?.let { it1 ->
                                    _state.update {
                                        it.copy(
                                            total = it1.price * state.quantity
                                        )
                                    }
                                }
                            }
                        }
                    }

                    fun Float.toFormattedValue(): String {
                        return this.toString().dropLast(2)
                    }
                    
                    data class TicketBuilderState(
                        val tickets: List<Ticket> = emptyList(),
                        val selectedTicketType: Ticket? = null,
                        val quantity: Int = 1,
                        val total: Float = 0f,
                    ) {
                        val purchageEnabled: Boolean
                            get() = selectedTicketType != null

                        val decreaseEnabled: Boolean
                            get() = quantity > 1
                    }

                    data class Ticket(
                        val title: String,
                        val price: Float,
                        val ticketType: TicketType,
                    )

                    enum class TicketType {
                        STANDART,
                        VIP,
                        BACKSTAGE
                    }
                    
                    sealed interface TicketBuilderAction {
                        data class OnTicketTypeSelected(val ticket: Ticket) : TicketBuilderAction
                        data object OnQuantityIncrease : TicketBuilderAction
                        data object OnQuantityDecrease : TicketBuilderAction
                    }
                    """.trimIndent()
            )

            KotlinCode(
                code = """
                    //Please not use operator invoke() in usecase
                    
                    class GetToken @Inject constructor(private val repository: TokenRepository) {
                        operator fun invoke(): Flow<Resource<AccessToken>> = flow {
                            try {
                                emit(Resource.Loading())
                                repository.fetchAccessToken()?.let { token ->
                                    emit(Resource.Success(data = token))
                                } ?: run {
                                    emit(Resource.Error(message = "Error getting token"))
                                }
                            } catch (e: HttpException) {
                                emit(Resource.Error(e.localizedMessage ?: "Unexpected error"))
                            } catch (e: IOException) {
                                emit(Resource.Error("Check your internet connection"))
                            }
                        }
                    }

                    interface TokenRepository {
                        suspend fun fetchAccessToken(): AccessToken?
                    }

                    import com.google.gson.annotations.SerializedName

                    data class AccessToken(
                        @SerializedName("access_token")
                        val accessToken: String,
                        @SerializedName("expires_in")
                        val expiresIn: Int,
                        @SerializedName("token_type")
                        val tokenType: String
                    )
                    """.trimIndent()
            )

            SpanText(
                text = "Do",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            KotlinCode(
                code = """
                    @Composable
                    fun Screen() {
                        // State has only on Screen
                        var checked by remember { mutableStateOf(false) }
                        Row {
                            MySwitch(checked = checked, onCheckChanged = { checked = it })
                            Text(
                                text = if (checked) "on" else "false",
                                Modifier.clickable {
                                    checked = !checked
                                }
                            )
                        }
                    }

                    // checked is Immutable, a value that cannot be changed.
                    @Composable
                    fun MySwitch(checked: Boolean, onCheckChanged: (Boolean) -> Unit) {
                        Switch(
                            checked = checked,
                            onCheckedChange = {
                                onCheckChanged(it)
                            }
                        )
                    }
                    """.trimIndent()
            )

            KotlinCode(
                code = """
                    @Composable
                    fun Screen() {
                        var checked by remember { mutableStateOf(false) }
                        MySwitch(checked = checked, onCheckChanged = { checked = it })
                    }

                    @Composable
                    fun MySwitch(checked: Boolean, onCheckChanged: (Boolean) -> Unit) {
                        Switch(
                            checked = checked,
                            onCheckedChange = {
                                onCheckChanged(it)
                            }
                        )
                    }
                    """.trimIndent()
            )

            KotlinCode(
                code = """
                    @Composable
                    fun MySwitch() {
                        var checked by remember { mutableStateOf(false) }
                        
                        Switch(
                            checked = checked,
                            onCheckedChange = {
                                checked = it
                            }
                        )
                    }
                    """.trimIndent()
            )

            //https://github.com/pddhkt/workout-app.git
            KotlinCode(
                code = """
                    //var currentReps by remember { mutableIntStateOf(0) }
                    //var currentWeight by remember { mutableFloatStateOf(0f) }
                    //var currentRPE by remember { mutableStateOf<Int?>(null) }
    
                    var favoriteExercises by remember { mutableStateOf(setOf<String>()) }
                    
                    favoriteExercises = if (favoriteExercises.contains(exercise.id)) {
                        favoriteExercises - exercise.id
                    } else {
                        favoriteExercises + exercise.id
                    }
                    """.trimIndent()
            )

            //https://github.com/xephosbot/AnilibriaRefresh.git
            KotlinCode(
                code = """
                    @OptIn(ExperimentalCoroutinesApi::class)
                    internal class SearchViewModel() : ViewModel() {
                        private val _selectedFilters = MutableStateFlow(SearchFiltersState())
                        val selectedFilters: StateFlow<SearchFiltersState> = _selectedFilters.asStateFlow()

                        //more than one update
                        private fun updateState(block: (SearchFiltersState) -> SearchFiltersState) {
                            _selectedFilters.update { block(_selectedFilters.value) }
                        }
                    }
                    """.trimIndent()
            )

            //https://github.com/amirroid/chatguard.git
            KotlinCode(
                code = """
                    interface StateBridge<T> {
                        val state: StateFlow<T>
                        fun updateState(newValue: T)
                    }
                    
                    class MessengerStateBridge : StateBridge<ExtractedMessengerChat?> {
                        private val _state = MutableStateFlow<ExtractedMessengerChat?>(null)
                        override val state: StateFlow<ExtractedMessengerChat?> = _state

                        override fun updateState(newValue: ExtractedMessengerChat?) {
                            _state.update { newValue }
                        }
                    }
                    
                    data class ExtractedMessengerChat(
                        val title: String = "",
                        val packageName: String = "",
                        val messages: List<ChatMessage> = emptyList()
                    )
                    
                    data class ChatMessage(
                        val text: String,
                        val date: String,
                        val sender: MessageSender
                    )

                    /**
                     * Message sender type
                     */
                    enum class MessageSender {
                        ME,
                        OTHER
                    }
                    """.trimIndent()
            )

            //https://medium.com/@abdullahkhan1571998
            KotlinCode(
                code = $$"""
                    class TimerTest() {
                        val scope = CoroutineScope(Dispatchers.IO)

                        private val _timer = MutableStateFlow(0)
                        val timer = _timer.asStateFlow()

                        fun startTimer() = scope.launch {

                            repeat(1000) { i ->
                                delay(1000)
                                _timer.update { i }
                                Log.e("TimerTest", "Timer running $i")
                            }

                        }

                    }

                    @Preview
                    @Composable
                    fun TimerComposablePreview() {

                        var show by remember {
                            mutableStateOf(true)
                        }

                        if (show) TimerComposable {
                            show = false
                        } else {
                            Text("Timer should be stopped")
                        }
                    }

                    @Composable
                    fun TimerComposable(onClick: () -> Unit) = Column(
                        modifier = Modifier
                            .fillMaxSize()
                            .background(Color.White)
                    ) {

                        var timer by remember {
                            mutableIntStateOf(0)
                        }

                        Text(modifier = Modifier, text = "$timer")

                        Button(modifier = Modifier, onClick = onClick) {
                            Text("Button Click")
                        }

                        LaunchedEffect(timer) {
                            Log.e("TimerTest", "Recomposed LaunchedEffect $timer")
                        }

                        DisposableEffect(Unit) {
                            val timerTest = TimerTest()

                            CoroutineScope(Dispatchers.IO).launch {
                                with(timerTest) {
                                    startTimer()
                                    this.timer.collect {
                                        timer = it
                                    }
                                }
                            }

                            onDispose {
                                Log.e("TimerTest", "Should stop")
                            }
                        }
                    }
                    """.trimIndent()
            )
        }
    }
}

//https://medium.com/droidstack/jetpack-compose-state-interview-questions-65494fe78ec6
//https://medium.com/droidstack/difference-between-remember-mutablelistof-vs-mutablestatelistof-368e672a50b4
//https://pl-coding.com/2024/11/16/composestate-vs-stateflow
//https://medium.com/@sivavishnu0705/1912eeaaae3f
//https://medium.com/@desiappdev24/state-management-in-jetpack-compose-8b8c5ebfc891
//https://medium.com/@droiddev5911/server-driven-ui-sdui-in-android-modern-app-architecture-939e385bc722
//https://www.myandroidsolutions.com/2026/03/02/kotlin-buildlist-buildmap-buildset-collection-builders/?utm_source=linkedin&utm_medium=jetpack_social
//https://proandroiddev.com/the-evolution-of-flow-collection-in-jetpack-compose-e93416a9d52d